<template>
  <div class="egresos-container">
    <!-- Tabla de Órdenes de Egreso -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="egresos-title">Gestión de Egresos</span>
        <div class="d-flex align-center flex-wrap ga-2">
          <v-btn
            color="success"
            variant="tonal"
            prepend-icon="mdi-microsoft-excel"
            size="default"
            @click="exportGs1Excel"
            title="Exportar movimientos de salida con claves GS1 (SSCC, GSIN, GTIN, lote) a Excel"
          >
            <span class="d-none d-sm-inline">Excel GS1</span>
          </v-btn>
          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            data-tour="egresos-nueva"
            @click="showNewEgresoModal"
            size="default"
          >
            <span class="d-none d-sm-inline">Nueva Salida</span>
            <span class="d-sm-none">Nueva</span>
          </v-btn>
        </div>
      </v-card-title>
      <!-- Último SSCC enviado (checklist 4.3) -->
      <v-card-subtitle v-if="lastSsccOut" class="pb-0">
        <v-chip size="small" color="indigo" variant="tonal">
          <v-icon start size="small">mdi-barcode</v-icon>
          Último SSCC enviado: {{ lastSsccOut.sscc }} → {{ lastSsccOut.customer || 'sin cliente' }} ({{ lastSsccOut.order }})
        </v-chip>
      </v-card-subtitle>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4 egresos-filters">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateTo"
              label="Fecha hasta"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Orden, cliente, RUT..."
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.gs1"
              prepend-inner-icon="mdi-barcode-scan"
              label="Clave GS1"
              placeholder="SSCC, GSIN, GTIN o lote"
              hide-details
              clearable
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Envío agrupado GSIN (checklist GS1: agrupa ≥2 SSCC en un packing list múltiple) -->
        <v-alert v-if="gsinSelection.length" type="info" variant="tonal" density="compact" class="mb-3">
          <div class="d-flex align-center flex-wrap ga-2">
            <span>{{ gsinSelection.length }} egreso(s) seleccionados para envío agrupado (mínimo 2).</span>
            <v-btn size="small" color="teal" :disabled="gsinSelection.length < 2"
                   prepend-icon="mdi-package-variant-closed" @click="downloadMultiPackingList">
              Packing List múltiple (GSIN)
            </v-btn>
            <v-btn size="small" variant="text" @click="gsinSelection = []">Limpiar</v-btn>
          </div>
        </v-alert>

        <!-- Tabla -->
        <ResponsiveDataTable
          :items="filteredEgresos"
          :headers="[
            { title: 'Agrupar', key: 'multi_select', align: 'center', sortable: false },
            { title: 'Número de Orden', key: 'order_number', align: 'start', sortable: true },
            { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
            { title: 'Cliente', key: 'customer.name', align: 'start', sortable: true },
            { title: 'RUT', key: 'customer.tax_id', align: 'start', sortable: true },
            { title: 'SSCC / GSIN', key: 'sscc', align: 'start', sortable: false },
            { title: 'Estrategia', key: 'strategy', align: 'center', sortable: true },
            { title: 'Ítems', key: 'items_count', align: 'end', sortable: true },
            { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
          ]"
          :loading="loading"
          :search="filters.search"
          class="elevation-1 egresos-table"
          :show-select="false"
          items-per-page="10"
          density="default"
          hover
        >
          <template v-slot:item.multi_select="{ item }">
            <v-checkbox-btn
              :model-value="gsinSelection.includes(item.id)"
              @update:model-value="toggleGsinSelection(item.id)"
              density="compact"
            ></v-checkbox-btn>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.sscc="{ item }">
            <div v-if="item.sscc" class="text-caption"><b>SSCC</b> {{ item.sscc }}</div>
            <div v-if="item.gsin" class="text-caption text-teal"><b>GSIN</b> {{ item.gsin }}</div>
            <span v-if="!item.sscc && !item.gsin" class="text-caption text-grey"
                  title="Egreso unitario sin pallet — el SSCC se emite marcando 'Armar pallet' al crear o al generar los documentos GS1 (mínimo 2 elementos)">—</span>
          </template>

          <template v-slot:item.customer.name="{ item }">
            {{ item.customer?.name || 'No especificado' }}
          </template>

          <template v-slot:item.customer.tax_id="{ item }">
            {{ item.customer?.tax_id || '-' }}
          </template>

          <template v-slot:item.strategy="{ item }">
            <v-chip :color="getStrategyColor(item.strategy)" size="small">
              {{ getStrategyText(item.strategy) }}
            </v-chip>
          </template>

          <template v-slot:item.items_count="{ item }">
            {{ item.items_count || 0 }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="viewEgreso(item)"
              color="primary"
              variant="text"
              class="mr-1"
            ></v-btn>
            <!-- Documentos GS1 del egreso (checklist 4.4): packing list + etiqueta SSCC -->
            <v-btn
              icon="mdi-clipboard-list-outline"
              size="small"
              @click="downloadEgressPackingList(item)"
              color="teal"
              variant="text"
              class="mr-1"
              title="Packing List GS1 (PDF)"
            ></v-btn>
            <v-btn
              icon="mdi-code-json"
              size="small"
              @click="downloadEgressPackingListJson(item)"
              color="teal"
              variant="text"
              class="mr-1"
              title="Packing List electrónico (JSON) — importable en Recepciones"
            ></v-btn>
            <v-btn
              icon="mdi-barcode"
              size="small"
              @click="printEgressSsccLabel(item)"
              color="indigo"
              variant="text"
              class="mr-1"
              title="Etiqueta logística GS1 con SSCC"
            ></v-btn>
            <v-btn
              icon="mdi-file-tree"
              size="small"
              @click="downloadEgressHierarchy(item)"
              color="deep-purple"
              variant="text"
              class="mr-1"
              title="Informe de Jerarquía GS1 (SSCC → GTIN-14 → GTIN-13)"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              @click="deleteEgreso(item)"
              color="error"
              variant="text"
            ></v-btn>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Etiqueta SSCC: AI de uso interno 90-99 opcional (checklist GS1 1.20) -->
    <v-dialog v-model="ssccLabelDialog.show" max-width="600">
      <v-card>
        <v-card-title>Etiqueta logística SSCC</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Opcional: incluir un AI de uso interno (90-99) en la etiqueta
            — por ejemplo un código de ruta o de cliente.
          </p>
          <v-row dense>
            <v-col cols="4">
              <v-select
                v-model="ssccLabelDialog.ai"
                :items="['90','91','92','93','94','95','96','97','98','99']"
                label="AI"
                density="compact"
              ></v-select>
            </v-col>
            <v-col cols="8">
              <v-text-field
                v-model="ssccLabelDialog.value"
                label="Valor (opcional)"
                maxlength="30"
                counter="30"
                density="compact"
                hint="Vacío = etiqueta sin AI de uso interno"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>
          <!-- Orden de los AIs del GS1-128 de contenido: GS1 no exige uno;
               sirve para demostrar que los lectores parsean por FNC1 -->
          <div v-if="ssccAiList.length > 1" class="d-flex align-center flex-wrap ga-1 mt-1">
            <span class="text-caption text-medium-emphasis mr-1">Orden de los AIs:</span>
            <v-chip v-for="(ai, i) in ssccAiList" :key="ai" size="small" variant="outlined" color="indigo" class="pr-1">
              ({{ ai }})
              <v-btn icon="mdi-chevron-left" size="x-small" variant="text" density="compact" :disabled="i === 0"
                     title="Mover antes" @click="moveSsccAi(i, -1)"></v-btn>
              <v-btn icon="mdi-chevron-right" size="x-small" variant="text" density="compact" :disabled="i === ssccAiList.length - 1"
                     title="Mover después" @click="moveSsccAi(i, 1)"></v-btn>
            </v-chip>
            <v-btn v-if="ssccLabelDialog.aiOrder.length" size="x-small" variant="text" @click="resetSsccAiOrder">Orden natural</v-btn>
          </div>
          <div v-if="ssccContentPreview" class="text-caption text-medium-emphasis mt-2">
            Contenido: <code>{{ ssccContentPreview }}</code><br>
            Máx. 48 caracteres por GS1-128 (con AIs y paréntesis): si excede se divide en 2 símbolos.
          </div>
          <ZebraPrinterSelect class="mt-3" />
        </v-card-text>
        <v-card-actions class="flex-wrap justify-end ga-1">
          <v-btn variant="text" size="small" @click="ssccLabelDialog.show = false">Cancelar</v-btn>
          <v-btn color="teal" variant="tonal" size="small" @click="downloadSsccLabelZpl"
                 title="ZPL nativo Zebra: la impresora dibuja las barras con precisión exacta de puntos (calidad de verificación). Imprimir con: lp -d <impresora> -o raw archivo.zpl">
            Descargar ZPL
          </v-btn>
          <v-btn color="teal" variant="flat" size="small" prepend-icon="mdi-printer-wireless"
                 :loading="zebraPrinting" @click="printSsccLabelZebra"
                 title="Envía el ZPL directo a la Zebra vía Browser Print (app local de Zebra) — sin diálogo de impresión ni terminal">
            Imprimir en Zebra
          </v-btn>
          <v-btn color="indigo" variant="flat" size="small" @click="confirmSsccLabel"
                 title="Impresión por navegador (rasterizada) — usar solo si no está la Zebra">
            Imprimir etiqueta
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Nueva Salida -->
    <v-dialog v-model="showCreateModal" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditingEgreso ? 'Editar Salida' : 'Nueva Salida' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="egresoForm" v-model="formValid">
            <!-- Cliente y Estrategia -->
            <v-row>
              <v-col cols="12" md="8">
                <!-- Sector salud: la salida va a un destino interno (servicio / pabellón / bodega de paso).
                     El egreso descuenta el inventario y la unidad pasa a custodia del destino (NGT 247 §7.1 f). -->
                <template v-if="sectorSalud">
                  <v-btn-toggle v-model="salidaModo" mandatory density="compact" color="teal" variant="outlined" class="mb-2">
                    <v-btn value="destino" size="small" prepend-icon="mdi-hospital-building">Destino interno</v-btn>
                    <v-btn value="cliente" size="small" prepend-icon="mdi-truck-delivery">Cliente externo</v-btn>
                  </v-btn-toggle>
                  <v-select
                    v-if="salidaModo === 'destino'"
                    v-model="newEgreso.destination_id"
                    :items="destinations"
                    item-title="name"
                    item-value="id"
                    label="Servicio / destino que solicita *"
                    variant="outlined"
                    density="compact"
                    :hint="destinations.length ? 'La unidad quedará en tránsito hasta que el destino la reciba' : 'Crea destinos en Destinos internos'"
                    persistent-hint
                  >
                    <template v-slot:item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps" :subtitle="item.raw.tipo_label"></v-list-item>
                    </template>
                  </v-select>
                  <ClienteSelector
                    v-else
                    v-model="newEgreso.customer_id"
                    @customer-selected="onCustomerSelected"
                    @customer-created="onCustomerCreated"
                  />
                </template>
                <ClienteSelector
                  v-else
                  v-model="newEgreso.customer_id"
                  @customer-selected="onCustomerSelected"
                  @customer-created="onCustomerCreated"
                />
              </v-col>
              <v-col cols="12" md="4">
                <!-- Estrategia heredada del producto (no se le pide al usuario) -->
                <div v-if="!mostrarOverrideEstrategia" class="d-flex align-center" style="height: 100%;">
                  <v-chip color="info" variant="tonal" size="small" prepend-icon="mdi-strategy">
                    Estrategia automática por producto
                  </v-chip>
                  <v-btn
                    v-if="hasPermission && hasPermission('inventory:manage')"
                    variant="text"
                    size="x-small"
                    color="secondary"
                    class="ml-2"
                    @click="mostrarOverrideEstrategia = true"
                  >
                    Forzar estrategia
                  </v-btn>
                </div>
                <!-- Override avanzado (solo Jefe de Bodega) -->
                <v-select
                  v-else
                  v-model="newEgreso.strategy"
                  :items="strategyOptions"
                  label="Forzar estrategia (avanzado)"
                  variant="outlined"
                  density="compact"
                  hint="Sobrescribe la estrategia de todos los ítems"
                  persistent-hint
                  @update:model-value="onStrategyChanged"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Información adicional -->
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="newEgreso.notes"
                  label="Notas adicionales"
                  rows="2"
                  variant="outlined"
                  density="compact"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Pallet SSCC opcional + peso (observación 1ª evaluación GS1) -->
            <v-row dense align="center">
              <v-col cols="12" md="4">
                <v-switch
                  v-model="newEgreso.build_pallet"
                  color="indigo"
                  density="compact"
                  hide-details
                  label="Armar pallet (emitir SSCC)"
                ></v-switch>
                <div class="text-caption text-grey">
                  Opcional — requiere mínimo 2 elementos; sin marcar es un egreso unitario
                  (el SSCC también puede emitirse después al generar los documentos GS1)
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field
                  v-model.number="newEgreso.net_weight_kg"
                  label="Peso neto (kg) — romana"
                  type="number"
                  min="0"
                  step="0.001"
                  density="compact"
                  hide-details
                ></v-text-field>
                <div class="text-caption mt-1">
                  <template v-if="suggestedNetWeight !== null">
                    <v-chip size="x-small" color="teal" variant="tonal"
                            @click="newEgreso.net_weight_kg = suggestedNetWeight"
                            title="Suma de los pesos (310x) escaneados × cantidades — clic para usar">
                      Sugerido por códigos: {{ suggestedNetWeight }} kg
                    </v-chip>
                  </template>
                  <span v-else class="text-grey">
                    Pesa el pallet e ingresa el valor
                    {{ newEgreso.items.length ? '(no todos los items traen peso (310x) en su código)' : '' }}
                  </span>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field
                  v-model.number="newEgreso.gross_weight_kg"
                  label="Peso bruto (kg)"
                  type="number"
                  min="0"
                  step="0.001"
                  density="compact"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Productos -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos</h3>
              <div class="d-flex gap-2">
                <v-btn
                  color="secondary"
                  prepend-icon="mdi-barcode"
                  @click="startSingleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData"
                >
                  Escanear Individual
                </v-btn>
                <v-btn
                  color="info"
                  prepend-icon="mdi-barcode-scan"
                  @click="startMultipleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData"
                >
                  Escaneo Masivo
                </v-btn>
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="addProductManually"
                  size="small"
                  :disabled="loadingInitialData"
                >
                  Agregar Manual
                </v-btn>
              </div>
            </div>

            <!-- Tabla de productos -->
            <v-card variant="outlined" class="mb-4" v-if="newEgreso.items.length > 0">
              <v-card-text class="pa-2">
                <v-table class="egresos-items-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="product-col">Producto</th>
                        <th class="sku-col">GTIN</th>
                        <th class="quantity-col">Cantidad</th>
                        <th class="lot-col">Lote</th>
                        <th class="expiry-col">Vencimiento</th>
                        <th class="serial-col">S/N</th>
                        <th class="production-col">F. Fabricación</th>
                        <th class="pack-col">F. Empaque</th>
                        <th class="sscc-col">SSCC</th>
                        <th class="gln-col">GLN</th>
                        <th v-if="mostrarPrecios" class="price-col">Precio unit.</th>
                        <th v-if="mostrarPrecios" class="price-col">Total</th>
                        <th class="strategy-col">Estrategia</th>
                        <th class="actions-col">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in newEgreso.items" :key="index">
                        <td class="product-col">
                          <div>{{ item.product_name || 'Producto no encontrado' }}</div>
                          <!-- Mostrar pesos y campos personalizados si existen -->
                          <div v-if="hasWeightOrCustomData(item)" class="text-caption text-grey-600 mt-1">
                            <span v-if="item.gross_weight_kg">Peso: {{ item.gross_weight_kg }}kg</span>
                            <span v-if="item.gross_weight_lb && !item.gross_weight_kg">Peso: {{ item.gross_weight_lb }}lb</span>
                            <span v-if="item.gdti" class="ms-2">GDTI: {{ item.gdti }}</span>
                            <span v-if="hasCustomFields(item)" class="ms-2">
                              <v-icon size="x-small" color="info">mdi-tag</v-icon>
                              {{ getCustomFieldsCount(item) }} campos
                            </span>
                          </div>
                        </td>
                        <td class="sku-col">{{ item.gtin || item.product_barcode || item.product_sku || '-' }}</td>
                        <td class="quantity-col">
                          <div>{{ item.quantity }}</div>
                          <!-- Línea armada por CAJAS (unidad de despacho) — checklist 4.2 -->
                          <v-chip v-if="item.cases_count" color="teal" size="x-small" variant="tonal"
                                  title="Pallet armado por unidades de despacho: la etiqueta declara el contenido a nivel de caja">
                            {{ item.cases_count }} {{ item.cases_count === 1 ? 'caja' : 'cajas' }}
                          </v-chip>
                        </td>
                        <!-- Sin lote en el código: al crear la salida se hereda del
                             stock que se descuenta (según la estrategia del producto) -->
                        <td class="lot-col">
                          <span v-if="item.lot_number">{{ item.lot_number }}</span>
                          <span v-else class="text-medium-emphasis font-italic"
                                :title="`Se asigna al crear la salida desde el stock descontado (${(item.picking_strategy || 'fifo').toUpperCase()})`">
                            del stock ({{ (item.picking_strategy || 'fifo').toUpperCase() }})
                          </span>
                        </td>
                        <td class="expiry-col">
                          <span v-if="item.expiry_date">{{ formatDate(item.expiry_date) }}</span>
                          <span v-else-if="!item.lot_number" class="text-medium-emphasis font-italic">del stock</span>
                          <span v-else>-</span>
                        </td>
                        <td class="serial-col">{{ item.serial_number || '-' }}</td>
                        <td class="production-col">{{ formatDate(item.production_date) || '-' }}</td>
                        <td class="pack-col">{{ formatDate(item.pack_date) || '-' }}</td>
                        <td class="sscc-col">{{ item.sscc || '-' }}</td>
                        <td class="gln-col">{{ item.gln || '-' }}</td>
                        <td v-if="mostrarPrecios" class="price-col">{{ formatMoney(itemUnitPrice(item)) }}</td>
                        <td v-if="mostrarPrecios" class="price-col font-weight-medium">{{ formatMoney(itemLineTotal(item)) }}</td>
                        <td class="strategy-col">
                          <div class="d-flex flex-column align-center" style="gap: 2px;">
                            <v-chip
                              :color="getStrategyColor(item.picking_strategy)"
                              size="x-small"
                              variant="tonal"
                            >
                              {{ getStrategyText(item.picking_strategy) }}
                            </v-chip>
                            <v-chip
                              v-if="!item.strategy_compliant"
                              color="warning"
                              size="x-small"
                              prepend-icon="mdi-alert"
                              @click="showStrategyWarning(item)"
                            >
                              No cumple
                            </v-chip>
                            <v-chip
                              v-else
                              color="success"
                              size="x-small"
                              prepend-icon="mdi-check"
                            >
                              Cumple
                            </v-chip>
                          </div>
                        </td>
                        <td class="actions-col">
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            @click="removeProduct(index)"
                            color="error"
                            variant="text"
                          ></v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-card-text>
            </v-card>

            <!-- Mensaje cuando no hay productos -->
            <v-alert
              v-if="newEgreso.items.length === 0"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              No hay productos agregados. Use los botones de escaneo o agregue productos manualmente.
            </v-alert>

            <!-- Panel de escaneo -->
            <v-card 
              v-if="showBarcodeScanner || isMultipleScanMode"
              variant="outlined"
              class="mb-4"
            >
              <v-card-title class="text-h6">
                {{ isMultipleScanMode ? 'Escaneo Masivo' : 'Escaneo Individual' }}
              </v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="multipleScanInput"
                  :label="isMultipleScanMode ? 'Pegue códigos aquí (uno por línea)' : 'Escanee o ingrese código'"
                  :rows="isMultipleScanMode ? 8 : 2"
                  variant="outlined"
                  density="compact"
                  @keyup.enter="isMultipleScanMode ? null : processScannedBarcode(multipleScanInput)"
                  :hint="isMultipleScanMode ? 'Pegue múltiples códigos, uno por línea' : 'Presione Enter para procesar'"
                  persistent-hint
                ></v-textarea>
                
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    color="primary"
                    @click="isMultipleScanMode ? processMultipleCodes() : processScannedBarcode(multipleScanInput)"
                    :disabled="!multipleScanInput.trim()"
                  >
                    {{ isMultipleScanMode ? 'Procesar Códigos' : 'Procesar Código' }}
                  </v-btn>
                  <v-btn
                    color="grey"
                    @click="closeBarcodeScanner"
                    variant="outlined"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    v-if="isMultipleScanMode"
                    color="info"
                    @click="clearMultipleScanInput"
                    variant="outlined"
                  >
                    Limpiar
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Loading inicial -->
            <v-card variant="outlined" v-if="loadingInitialData" class="mb-4">
              <v-card-text class="text-center">
                <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                <div>Cargando datos del sistema...</div>
              </v-card-text>
            </v-card>

            <!-- Resumen -->
            <v-card variant="outlined" v-if="newEgreso.items.length > 0">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8">
                    <div class="text-h6">Resumen</div>
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <div>
                      <strong>Total de ítems:</strong> {{ newEgreso.items.length }}
                    </div>
                    <div>
                      <strong>Total de Unidades:</strong> {{ totalQuantity }}
                    </div>
                    <div v-if="mostrarPrecios" class="text-h6 mt-1">
                      <strong>Total valorizado:</strong> {{ formatMoney(egresoTotalValorizado) }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCreateModal">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary"
            @click="saveEgreso"
            :loading="submitting"
            :disabled="!canSaveEgreso"
          >
            {{ isEditingEgreso ? 'Actualizar' : 'Crear Salida' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de advertencia de estrategia -->
    <v-dialog v-model="showStrategyWarningDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Advertencia de Estrategia
        </v-card-title>
        
        <v-card-text>
          <v-alert type="warning" class="mb-3">
            {{ strategyWarningMessage }}
          </v-alert>
          
          <div v-if="suggestedProduct" class="mt-3">
            <h4>Producto Sugerido:</h4>
            <v-card variant="outlined" class="mt-2">
              <v-card-text>
                <div><strong>{{ suggestedProduct.type === 'lot' ? 'Lote' : 'Fecha de Vencimiento' }}:</strong> {{ suggestedProduct.value }}</div>
                <div><strong>Cantidad Disponible:</strong> {{ suggestedProduct.available_quantity }} unidades</div>
                <div v-if="suggestedProduct.date"><strong>Fecha:</strong> {{ formatDate(suggestedProduct.date) }}</div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="outlined" @click="ignoreStrategyWarning">
            IGNORAR ESTRATEGIA
          </v-btn>
          <v-btn color="success" @click="acceptSuggestedProduct">
            USAR SUGERIDO
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Lote escaneado sin stock: ofrecer los otros lotes con saldo -->
    <v-dialog v-model="showLotPickerDialog" max-width="640px" persistent>
      <v-card v-if="lotPicker">
        <v-card-title class="d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-package-variant-closed-remove</v-icon>
          Lote sin stock disponible
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
            {{ lotPicker.message }}
          </v-alert>
          <div class="mb-2">
            Se necesitan <strong>{{ lotPicker.qty }}</strong> un. de
            <strong>{{ lotPicker.product.name }}</strong>
            <span v-if="lotPicker.isCase">({{ lotPicker.cases }} caja × {{ lotPicker.product.units_per_case }} un.)</span>.
            Elige el lote del que se despachará (saldo = stock disponible − lo ya agregado en esta salida):
          </div>
          <v-list density="compact" lines="two">
            <v-list-item
              v-for="alt in lotPicker.alternatives"
              :key="alt.lot"
              :disabled="!alt.enough"
              @click="pickAlternativeLot(alt)"
              rounded
            >
              <template #prepend>
                <v-icon :color="alt.enough ? 'success' : 'grey'">
                  {{ alt.enough ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
                </v-icon>
              </template>
              <v-list-item-title>
                {{ alt.lot ? `Lote ${alt.lot}` : 'Sin lote' }}
                <span v-if="alt.expiry" class="text-medium-emphasis"> · vence {{ formatDate(alt.expiry) }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                Saldo {{ alt.remaining }} un.
                <span v-if="!alt.enough"> — insuficiente para {{ lotPicker.qty }} un.</span>
                <span v-else-if="alt.remaining === lotPicker.qty"> — queda en 0</span>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeLotPicker">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Reporte de Egreso -->
    <v-dialog v-model="showReportModal" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Reporte de Salida</span>
          <v-btn
            icon="mdi-printer"
            @click="printReport"
            color="primary"
            variant="text"
            size="small"
          ></v-btn>
        </v-card-title>

        <v-card-text v-if="selectedEgreso" class="report-content">
          <!-- Información del Pedido -->
          <div class="report-header mb-4">
            <h3>Orden de Salida: {{ selectedEgreso.order_number }}</h3>
            <div class="mt-2">
              <strong>Fecha:</strong> {{ formatDate(selectedEgreso.created_at) }}<br>
              <strong>Cliente:</strong> {{ selectedEgreso.customer?.name || 'No especificado' }}<br>
              <strong>RUT:</strong> {{ selectedEgreso.customer?.tax_id || '-' }}<br>
              <strong>Estrategia:</strong> {{ getStrategyText(selectedEgreso.strategy) }}<br>
              <strong>Usuario:</strong> {{ selectedEgreso.user || 'Sistema' }}<br>
              <strong>Notas:</strong> {{ selectedEgreso.notes || 'Sin notas' }}
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Tabla de Productos -->
          <h4 class="mb-3">Productos</h4>
          <v-table density="compact" class="report-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>GTIN</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
                <th>Otros datos del código</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedEgreso.items" :key="item.id">
                <td>{{ item.product?.name || 'Producto desconocido' }}</td>
                <td>{{ item.gtin || item.product?.gtin || item.product?.barcode || '-' }}</td>
                <td>
                  {{ item.quantity }}
                  <v-chip v-if="item.cases_count" color="teal" size="x-small" variant="tonal" class="ml-1">
                    {{ item.cases_count }} {{ item.cases_count === 1 ? 'caja' : 'cajas' }}
                  </v-chip>
                </td>
                <td>{{ item.lot_number || '-' }}</td>
                <td>{{ formatDate(item.expiry_date) || '-' }}</td>
                <td>{{ item.serial_number || '-' }}</td>
                <td>{{ formatDate(item.production_date) || '-' }}</td>
                <!-- Passthrough: todo AI escaneado que no tiene columna propia -->
                <td class="text-caption">
                  <template v-if="(item.gs1_extra || []).filter(x => !['21', '11'].includes(x.ai)).length">
                    <div v-for="x in item.gs1_extra.filter(x => !['21', '11'].includes(x.ai))" :key="x.ai + x.value">
                      <b>{{ x.label }}:</b> {{ x.value }}
                    </div>
                  </template>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- Resumen -->
          <v-divider class="my-4"></v-divider>
          <div class="report-summary">
            <div class="d-flex justify-space-between">
              <strong>Total de ítems:</strong>
              <span>{{ selectedEgreso.items?.length || 0 }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <strong>Total de Unidades:</strong>
              <span>{{ selectedEgreso.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0 }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="printReport" prepend-icon="mdi-printer">
            Imprimir
          </v-btn>
          <v-btn color="grey darken-1" text @click="showReportModal = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ClienteSelector from '@/components/ClienteSelector.vue'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import api from '@/api'
import { mapGetters } from 'vuex'
import { expandUPCE, isValidGS1Key, isAI01Only } from '@/utils/gs1Validation'
import { buildLogisticLabelHTML, buildMultiPackingListHTML } from '@/utils/gs1Barcode'
import { buildLogisticLabelZPL, profileForPrinter, logisticLabelAIs, logisticContentParts, orderAIs, splitLogisticGroups } from '@/utils/zplLabel'
import { printZplViaBrowserPrint, resolveBrowserPrintTarget, getSavedPrinter } from '@/utils/browserPrint'
import ZebraPrinterSelect from '@/components/ZebraPrinterSelect.vue'

export default {
  name: 'Egresos',
  components: {
    ZebraPrinterSelect,
    ClienteSelector,
    ResponsiveDataTable
  },
  
  data() {
    return {
      // Filtros
      filters: {
        dateFrom: '',
        dateTo: '',
        search: '',
        gs1: ''
      },
      
      // Estado principal
      egresos: [],
      loading: false,
      // Egresos seleccionados para envío agrupado GSIN (packing list múltiple)
      gsinSelection: [],
      // Diálogo previo a la etiqueta SSCC: AI 90-99 de uso interno opcional
      // aiOrder: orden de los AIs del GS1-128 de contenido ([] = natural);
      // packingList: se carga al abrir el diálogo para mostrar los chips
      ssccLabelDialog: { show: false, egreso: null, ai: '91', value: '', aiOrder: [], packingList: null },
      // Enviando ZPL a la Zebra vía Browser Print
      zebraPrinting: false,
      
      // Datos precargados
      allProducts: [],
      inventoryData: [],
      loadingInitialData: false,
      
      // Modal crear/editar
      showCreateModal: false,
      isEditingEgreso: false,
      formValid: false,
      submitting: false,
      
      // Sector salud: destino interno (F4 trazabilidad médica)
      salidaModo: 'destino',
      destinations: [],
      // Formulario de egreso
      newEgreso: {
        customer_id: null,
        destination_id: null,
        strategy: 'libre',  // override global (solo si mostrarOverrideEstrategia); por defecto cada item usa la estrategia de su producto
        notes: '',
        items: [],
        // Pallet OPCIONAL (observación 1ª evaluación GS1): al marcar, se
        // emite el SSCC correlativo al crear (mínimo 2 elementos)
        build_pallet: false,
        net_weight_kg: null,
        gross_weight_kg: null
      },

      // Override avanzado de estrategia (solo Jefe de Bodega). Por defecto la
      // estrategia se hereda de cada producto y no se le pide al usuario.
      mostrarOverrideEstrategia: false,

      // Estrategias de picking
      strategyOptions: [
        { 
          value: 'libre', 
          title: 'Libre', 
          subtitle: 'Sin restricciones de estrategia' 
        },
        { 
          value: 'fifo', 
          title: 'FIFO', 
          subtitle: 'Primero en Entrar, Primero en Salir' 
        },
        { 
          value: 'fefo', 
          title: 'FEFO', 
          subtitle: 'Primero en Vencer, Primero en Salir' 
        },
        { 
          value: 'lifo', 
          title: 'LIFO', 
          subtitle: 'Último en Entrar, Primero en Salir' 
        }
      ],
      
      // Escaneo de códigos
      showBarcodeScanner: false,
      isMultipleScanMode: false,
      multipleScanInput: '',
      
      // Advertencias de estrategia
      showStrategyWarningDialog: false,
      // Escaneo pendiente cuando el lote leído no tiene stock (se ofrece otro lote)
      showLotPickerDialog: false,
      lotPicker: null,
      strategyWarningMessage: '',
      suggestedProduct: null,
      currentValidatingItem: null, // Item que está siendo validado
      
      // Modal de reporte
      showReportModal: false,
      selectedEgreso: null,
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones GS1
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" }, // Identificador del producto
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" }, // Fecha de producción
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" }, // Fecha de empaquetado
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" }, // Fecha de consumo preferente
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" }, // Fecha de vencimiento
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "330", len: 6,  fixed: true,  name: "Gross weight (kg, 6d)", wildcard: true }, // 330x - GS1 REQUERIDO
        { key: "340", len: 6,  fixed: true,  name: "Gross weight (lb, 6d)", wildcard: true }, // 340x - GS1 REQUERIDO
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        { key: "414", len: 13, fixed: true,  name: "GLN" }, // Global Location Number - GS1 REQUERIDO
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 }, // Lote
        { key: "21", fixed: false, name: "Serial",    max: 20 }, // Número de serie
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "90",  fixed: false, name: "Uso interno 90 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "91",  fixed: false, name: "Uso interno 91 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "92",  fixed: false, name: "Uso interno 92 (hasta 30)", max: 30 }, // GS1 REQUERIDO  
        { key: "93",  fixed: false, name: "Uso interno 93 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "94",  fixed: false, name: "Uso interno 94 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "95",  fixed: false, name: "Uso interno 95 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "96",  fixed: false, name: "Uso interno 96 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "97",  fixed: false, name: "Uso interno 97 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "98",  fixed: false, name: "Uso interno 98 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "99",  fixed: false, name: "Uso interno 99 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
        { key: "253", fixed: false, name: "GDTI + Serial", max: 30 }, // Global Document Type Identifier - GS1 REQUERIDO
        { key: "402", len: 17, fixed: true, name: "GSIN" }, // Global Shipment Identification Number - GS1 REQUERIDO
        { key: "401", fixed: false, name: "GINC", max: 30 }, // Global Identification Number for Consignment - GS1 RECOMENDADO
        { key: "8003", fixed: false, name: "GRAI", max: 30 }, // Global Returnable Asset Identifier - GS1 RECOMENDADO
        { key: "8004", fixed: false, name: "GIAI", max: 30 }, // Global Individual Asset Identifier - GS1 RECOMENDADO
        { key: "8018", len: 18, fixed: true, name: "GSRN" }, // Global Service Relation Number - GS1 RECOMENDADO
      ]
    }
  },
  
  computed: {
    // AIs del GS1-128 de contenido de la etiqueta SSCC, en el orden actual
    ssccAiList() {
      const d = this.ssccLabelDialog
      if (!d.packingList) return []
      const extraAI = d.value ? { ai: d.ai, value: d.value } : null
      return orderAIs(logisticLabelAIs(d.packingList, extraAI), d.aiOrder)
    },
    // Texto legible de los símbolos de contenido (≤ 48 chars c/u, hasta 2)
    ssccContentPreview() {
      const d = this.ssccLabelDialog
      if (!d.packingList) return ''
      const extraAI = d.value ? { ai: d.ai, value: d.value } : null
      const parts = orderAIs(logisticContentParts(d.packingList, extraAI), d.aiOrder, p => p.ai)
      return splitLogisticGroups(parts, { maxChars: 48 }).map(g => g.map(p => p.human).join('')).join('  |  ')
    },
    ...mapGetters('auth', ['hasPermission']),
    ...mapGetters('edition', ['manejaPrecios', 'sectorSalud']),

    // Mostrar precios solo si la edición los maneja Y el rol puede verlos
    // (un picker/operario nunca ve precios, aunque la edición los maneje).
    mostrarPrecios() {
      return this.manejaPrecios && (!this.hasPermission || this.hasPermission('sales_price:view'))
    },

    // Precio unitario del producto de un ítem (desde el catálogo cargado)
    egresoTotalValorizado() {
      return this.newEgreso.items.reduce((sum, item) => sum + this.itemLineTotal(item), 0)
    },

    filteredEgresos() {
      let filtered = this.egresos

      if (this.filters.dateFrom) {
        filtered = filtered.filter(e => e.created_at >= this.filters.dateFrom)
      }

      if (this.filters.dateTo) {
        filtered = filtered.filter(e => e.created_at <= this.filters.dateTo)
      }

      // Filtro ágil por clave GS1 (checklist 4.3): SSCC, GSIN, GTIN o lote —
      // escanea o escribe la clave y se detecta contra todos los campos
      if (this.filters.gs1) {
        const needle = this.filters.gs1.trim().toLowerCase()
        const toG14 = c => String(c || '').replace(/\D/g, '').padStart(14, '0')
        const needle14 = /^\d{8,14}$/.test(needle) ? toG14(needle) : null
        filtered = filtered.filter(e =>
          (e.sscc || '').toLowerCase().includes(needle) ||
          (e.gsin || '').toLowerCase().includes(needle) ||
          (e.items || []).some(it =>
            String(it.lot_number || '').toLowerCase().includes(needle) ||
            (needle14 && (toG14(it.gtin) === needle14 ||
                          toG14(it.product_barcode || (it.product && it.product.barcode)) === needle14))))
      }

      return filtered
    },

    // Suma de pesos (310x) escaneados × cantidad — SOLO con cobertura
    // completa: si a un item le falta el peso, la suma parcial no sirve
    // (regla de Juan: el peso real del pallet lo da la romana)
    suggestedNetWeight() {
      const items = this.newEgreso.items
      if (!items.length) return null
      let sum = 0
      for (const it of items) {
        const w = Number(it.net_weight_kg)
        if (!w || w <= 0) return null
        sum += w * (it.quantity || 0)
      }
      return Math.round(sum * 1000) / 1000
    },

    // "Último SSCC enviado, a qué cliente" (pregunta del auditor, checklist 4.3)
    lastSsccOut() {
      const conSscc = this.egresos.filter(e => e.sscc)
      if (!conSscc.length) return null
      const last = [...conSscc].sort((a, b) => String(b.completed_at || b.created_at || '').localeCompare(String(a.completed_at || a.created_at || '')))[0]
      return { sscc: last.sscc, customer: last.customer?.name, order: last.order_number }
    },
    
    canSaveEgreso() {
      const destinoOk = this.sectorSalud && this.salidaModo === 'destino' ? !!this.newEgreso.destination_id : !!this.newEgreso.customer_id
      return destinoOk && 
             this.newEgreso.items.length > 0 && 
             this.formValid
    },
    
    totalQuantity() {
      return this.newEgreso.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    }
  },
  
  async created() {
    if (this.sectorSalud) { try { this.destinations = (await api.getDestinations()).data.filter(d => d.activo) } catch (e) { this.destinations = [] } }
    await this.loadEgresos()
  },
  
  methods: {
    // Cargar datos
    async loadInitialData() {
      if (this.loadingInitialData) return
      
      try {
        this.loadingInitialData = true
        console.log('🔄 Cargando datos iniciales...')
        
        // Cargar productos y inventario en paralelo
        const [productsResponse, inventoryResponse] = await Promise.all([
          api.get('/products'),
          api.get('/inventory')
        ])
        
        this.allProducts = productsResponse.data || []
        this.inventoryData = inventoryResponse.data || []
        
        console.log(`✅ Datos cargados: ${this.allProducts.length} productos, ${this.inventoryData.length} items de inventario`)
        
      } catch (error) {
        console.error('❌ Error cargando datos iniciales:', error)
        this.showError('Error al cargar datos del sistema')
      } finally {
        this.loadingInitialData = false
      }
    },
    
    async loadEgresos() {
      try {
        this.loading = true
        const response = await api.get('/egresos/egress-orders', {
          params: this.filters
        })
        this.egresos = response.data || []
      } catch (error) {
        console.error('Error loading egresos:', error)
        this.showError('Error al cargar órdenes de egreso')
      } finally {
        this.loading = false
      }
    },
    
    // Modal handlers
    async showNewEgresoModal() {
      this.resetEgresoForm()
      this.isEditingEgreso = false
      this.showCreateModal = true
      
      // Cargar datos necesarios para el escaneo
      await this.loadInitialData()
    },
    
    closeCreateModal() {
      this.showCreateModal = false
      this.resetEgresoForm()
    },
    
    resetEgresoForm() {
      this.mostrarOverrideEstrategia = false
      this.newEgreso = {
        customer_id: null,
        destination_id: null,
        strategy: 'libre',
        notes: '',
        items: [],
        build_pallet: false,
        net_weight_kg: null,
        gross_weight_kg: null
      }
      this.formValid = false
      this.closeBarcodeScanner()
    },
    
    // Customer handlers
    onCustomerSelected(customer) {
      console.log('Customer selected:', customer)
    },
    
    onCustomerCreated(customer) {
      console.log('Customer created:', customer)
      this.showSuccess(`Cliente ${customer.name} creado exitosamente`)
    },
    
    // Strategy handlers
    // Estrategia heredada del producto (default FEFO si controla vencimiento, FIFO si no)
    getProductStrategy(product) {
      if (!product) return 'fifo'
      return product.picking_strategy || (product.requires_expiry_control ? 'fefo' : 'fifo')
    },

    // Precio de venta unitario del producto del ítem (desde el catálogo cargado)
    itemUnitPrice(item) {
      const product = this.allProducts.find(p => p.id === item.product_id)
      return (product && product.unit_price) || 0
    },

    itemLineTotal(item) {
      return this.itemUnitPrice(item) * (item.quantity || 0)
    },

    formatMoney(value) {
      const n = Number(value) || 0
      return '$' + n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
    },

    onStrategyChanged(strategy) {
      console.log('Strategy override changed:', strategy)
      // Override avanzado: sobrescribe la estrategia heredada de todos los ítems
      this.newEgreso.items.forEach(item => { item.picking_strategy = strategy })
      // Revalidar todos los productos cuando cambie la estrategia
      if (this.newEgreso.items.length > 0) {
        this.validateAllProductStrategies()
      }
    },

    async validateAllProductStrategies() {
      for (let item of this.newEgreso.items) {
        await this.validateProductStrategy(item)
      }
    },

    async validateProductStrategy(item) {
      // Estrategia efectiva: la del propio ítem (heredada del producto o forzada por override)
      const strategy = item.picking_strategy || 'libre'
      if (strategy === 'libre') {
        item.strategy_compliant = true
        item.strategy_warning = null
        return
      }

      try {
        console.log(`🔍 Validando estrategia ${strategy} para producto ${item.product_id}`)

        // Obtener items disponibles del producto
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableItems = response.data

        console.log('📦 Items disponibles:', availableItems)

        let compliant = true
        let warning = null
        let suggestedItem = null

        if (strategy === 'fifo') {
          ({ compliant, warning, suggestedItem } = this.validateFIFOStrategy(item, availableItems))
        } else if (strategy === 'lifo') {
          ({ compliant, warning, suggestedItem } = this.validateLIFOStrategy(item, availableItems))
        } else if (strategy === 'fefo') {
          // Solo validar FEFO si hay fecha de vencimiento
          if (!item.expiry_date) {
            console.log('⚠️ FEFO: No hay fecha de vencimiento, saltando validación')
            compliant = true
            warning = null
            suggestedItem = null
          } else {
            ({ compliant, warning, suggestedItem } = this.validateFEFOStrategy(item, availableItems))
          }
        }
        
        item.strategy_compliant = compliant
        item.strategy_warning = warning
        item.suggested_product = suggestedItem
        
        if (!compliant) {
          console.log(`⚠️ Estrategia no cumplida: ${warning}`)
        } else {
          console.log(`✅ Estrategia cumplida para ${strategy}`)
        }
        
      } catch (error) {
        console.error('❌ Error validando estrategia:', error)
        item.strategy_compliant = true // En caso de error, permitir el producto
      }
    },
    
    // Barcode scanning
    startSingleScan() {
      this.isMultipleScanMode = false
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
    },
    
    startMultipleScan() {
      this.isMultipleScanMode = true
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
    },
    
    closeBarcodeScanner() {
      this.showBarcodeScanner = false
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
    },
    
    clearMultipleScanInput() {
      this.multipleScanInput = ''
    },
    
    async processMultipleCodes() {
      const codes = this.multipleScanInput.split('\n').filter(code => code.trim())
      
      for (const code of codes) {
        await this.processScannedBarcode(code.trim())
      }
      
      this.closeBarcodeScanner()
    },
    
    async processScannedBarcode(code) {
      if (!code || !code.trim()) return
      
      console.log(`🔍 Procesando código de barras: ${code}`)
      
      // Verificar que los datos estén cargados
      if (this.allProducts.length === 0) {
        console.log('⚠️ Los datos no están cargados, cargando...')
        await this.loadInitialData()
      }
      
      try {
        // Analizar el código
        const analysis = this.analyzeBarcode(code.trim())
        console.log('📊 Análisis del código:', analysis)
        
        let product = null
        let productData = {}
        
        if (analysis.type === 'GS1') {
          console.log(`🏷️ Código GS1 detectado, GTIN: ${analysis.gtin}`)
          // Buscar producto por GTIN
          product = this.findProductByGTIN(analysis.gtin)
          productData = analysis.variableData || {}
          console.log('📦 Datos variables extraídos:', productData)
        } else {
          console.log(`📋 Código estándar detectado: ${code}`)
          // Buscar producto por código de barras normal
          product = this.findProductByBarcode(code)
        }
        
        if (product) {
          console.log(`✅ Producto encontrado: ${product.name} (ID: ${product.id})`)
          // El código crudo se conserva (barcode_scanned); el GTIN resuelto del
          // (01) va aparte para reconocer la caja (isCaseScan). En GS1 se envían
          // además TODOS los AIs tal cual, para verlos en detalle y documentos
          if (analysis.type === 'GS1') {
            try {
              productData = { ...productData, gs1_items: this.parseGS1(code).items.map(i => ({ ai: i.ai, value: i.value })) }
            } catch (e) { /* sin passthrough si el parser falla */ }
          }
          await this.addScannedProduct(product, productData, code, analysis.gtin)
        } else {
          console.log(`❌ Producto NO encontrado para código: ${code}`)
          console.log(`🔍 Productos disponibles: ${this.allProducts.length}`)
          
          // En lugar de mostrar error, mostrar información del código escaneado
          if (analysis.type === 'GS1' && productData && Object.keys(productData).length > 0) {
            // Es un código GS1 válido pero no comercial - mostrar información
            this.showCodeInformation(analysis, productData)
          } else if (analysis.type === 'GS1' && analysis.gtin) {
            // Es GS1 pero solo con GTIN - mostrar información básica
            this.showBasicCodeInformation(analysis)
          } else {
            // Código no GS1 o sin datos válidos - mostrar error tradicional
            this.showError(`Producto no encontrado para el código: ${code}`)
          }
        }
        
      } catch (error) {
        console.error('❌ Error procesando código:', error)
        this.showError(`Error procesando código: ${code}`)
      }
      
      // Limpiar input en modo individual
      if (!this.isMultipleScanMode) {
        this.multipleScanInput = ''
      }
    },
    
    // ========== MÉTODOS DE ANÁLISIS DE CÓDIGOS DE BARRAS ==========
    
    // Detectar tipo de código de barras y extraer GTIN si es GS1
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      let cleaned = barcode.trim()

      // Prefijo AIM Code ID del lector (réplica del análisis de Ingresos):
      // ]C1/]d2/]Q3/]e0-e3 = GS1 garantizado; ]E0/]E4/]C0/etc. = NO GS1
      let aimGS1 = null
      const aimMatch = /^[\]+](C[01]|e[0-3]|E[0-4]|d2|Q3|A0)/.exec(cleaned)
      if (aimMatch) {
        cleaned = cleaned.slice(aimMatch[0].length)
        aimGS1 = ['C1', 'd2', 'Q3', 'e0', 'e1', 'e2', 'e3'].includes(aimMatch[1])
        console.log(`🏷️ AIM detectado ${aimMatch[0]} → ${aimGS1 ? 'GS1' : 'NO GS1'}`)
      }

      console.log('🧪 Analizando código:', {
        original: cleaned,
        length: cleaned.length,
        startsWithAI01: cleaned.startsWith('01'),
        hasParens: cleaned.includes('(')
      })

      // Detección GS1 mejorada: el AIM del lector manda; sin AIM, heurísticas
      const isGS1 = aimGS1 === true || (aimGS1 !== false && (
                   cleaned.includes('(') ||
                   (cleaned.startsWith('01') && cleaned.length > 16) ||
                   isAI01Only(cleaned) ||
                   (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                   (cleaned.includes(String.fromCharCode(29))) // Incluir códigos con separador FNC1
      ))
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0
        
        console.log('✅ Identificado como GS1:', {
          gtin: gtin,
          hasVariableData: hasVariableData,
          variableData: variableData
        })
        
        return { 
          type: 'GS1', 
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData
        }
      }
      
      // Solo evaluar códigos de longitud fija si NO es GS1
      // EAN-13 (exactamente 13 dígitos)
      if (/^\d{13}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-13:', cleaned)
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // UPC-A (exactamente 12 dígitos)
      if (/^\d{12}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC-A:', cleaned)
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // ITF-14 (exactamente 14 dígitos)
      if (/^\d{14}$/.test(cleaned)) {
        console.log('📊 Identificado como ITF-14:', cleaned)
        return { type: 'ITF-14', gtin: cleaned, original: cleaned, variableData: null }
      }

      // 8 dígitos: UPC-E (comprimido, se expande a UPC-A) o EAN-8
      if (/^\d{8}$/.test(cleaned)) {
        if (!isValidGS1Key(cleaned, [8])) {
          const upca = expandUPCE(cleaned)
          if (upca) {
            console.log('📊 Identificado como UPC-E, expandido a UPC-A:', cleaned, '→', upca)
            return { type: 'UPC-E', gtin: upca, original: cleaned, variableData: null }
          }
        }
        console.log('📊 Identificado como EAN-8:', cleaned)
        return { type: 'EAN-8', gtin: cleaned, original: cleaned, variableData: null }
      }

      // Otros códigos
      console.log('📋 Código no identificado específicamente:', cleaned)
      return { type: 'Other', barcode: cleaned, original: cleaned, variableData: null }
    },
    
    // Extraer GTIN y datos variables de código GS1
    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      let cleaned = gs1Code.replace(/[()]/g, '') // Remover paréntesis si existen
      cleaned = this.normalizeFNC1(cleaned)      // Normalizar FNC1 desde escáner DS2278
      let gtin = null
      let variableData = {}
      
      console.log('🔧 Extrayendo GTIN de:', cleaned)
      console.log('🔧 Código después de normalizar FNC1:', cleaned.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      // Log del código normalizado para debug
      if (window.debugGS1) {
        console.log('🚨 DEBUG GS1: Código detectado')
        console.log('Código completo:', cleaned)
        console.log('¿Contiene FNC1?', cleaned.includes(this.GS))
      }
      
      try {
        // Método mejorado según especificaciones del usuario:
        // "Si el código empieza con 01 los siguientes 14 caracteres son el GTIN"
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          // Extraer exactamente los siguientes 14 caracteres después de "01"
          gtin = cleaned.substring(2, 16) // Posiciones 2-15 (14 dígitos del GTIN)
          const remainingData = cleaned.substring(16) // Todo lo que viene después del GTIN
          
          console.log('✅ GTIN extraído según especificación (01 + 14 chars):', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            // Parsear datos variables que vienen después del GTIN
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          // Métodos alternativos para otros casos
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1] // GTIN de 14 dígitos
            const remainingData = gtinMatch[2] // Datos adicionales después del GTIN
            
            console.log('✅ GTIN extraído (regex):', gtin)
            console.log('🔍 Datos restantes para parsing:', remainingData)
            
            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          } else {
            // Código GS1 que NO comienza con (01): parsear todo como secuencia de AIs
            // (ej: (02) GTIN contenido, solo lote, SSCC). Igual que en Ingresos.
            const parsedData = this.parseGS1VariableData(cleaned)
            if (Object.keys(parsedData).length > 0) {
              variableData = parsedData
              console.log('✅ Secuencia de AIs sin (01) parseada:', parsedData)
              // (02) GTIN contenido identifica el producto de la unidad logística
              // (checklist GS1 3.3: ej. (02)GTIN(10)LOTE(17)VENC(37)CANT)
              if (parsedData.content_gtin) {
                gtin = parsedData.content_gtin
                console.log('✅ GTIN contenido (02) usado como identificador de producto:', gtin)
              }
            } else if (/^\d+$/.test(cleaned) && cleaned.length > 14) {
              // Si es un código largo sin AI visible, intentar extraer los primeros 14 dígitos
              gtin = cleaned.substring(0, 14)
              console.log('⚠️ GTIN extraído por longitud:', gtin)
            }
          }
        }

        // Si no se puede extraer GTIN, usar el código original
        if (!gtin) {
          console.log('❌ No se pudo extraer GTIN, usando código completo')
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      console.log('🎯 Resultado final - GTIN:', gtin, 'Variables:', variableData)
      return { gtin, variableData }
    },
    
    // ========== PARSER GS1 PROFESIONAL ==========
    
    // Función de utilidad para parsear fechas GS1 (YYMMDD → YYYY-MM-DD)
    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        console.warn(`⚠️ Fecha GS1 inválida (${aiType}):`, dateValue)
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      // Validar rangos básicos
      const monthNum = parseInt(mm)
      const dayNum = parseInt(dd)
      
      if (monthNum < 1 || monthNum > 12) {
        console.warn(`⚠️ Mes inválido en fecha GS1 (${aiType}):`, dateValue, `→ mm=${mm}`)
        return null
      }
      
      // Corregir día 00 a 01 para fechas válidas
      if (dd === '00') {
        dd = '01'
        console.log(`📅 Corrigiendo día 00 → 01 en fecha ${aiType}: ${dateValue} → ${yy}${mm}${dd}`)
      }
      
      // Validar día después de corrección
      if (dayNum < 0 || dayNum > 31) {
        console.warn(`⚠️ Día inválido en fecha GS1 (${aiType}):`, dateValue, `→ dd=${dd}`)
        return null
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}T00:00:00`
      console.log(`📅 Fecha GS1 parseada (${aiType}): ${dateValue} → ${parsedDate}`)
      return parsedDate
    },
    
    // Construir índices de búsqueda
    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      console.log(`🔍 findAI en pos ${pos}: chunk2="${chunk2}", chunk3="${chunk3}", chunk4="${chunk4}"`)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          console.log(`✅ AI wildcard encontrado: ${s.slice(pos, pos + 4)} (base: ${def.key})`)
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) {
        console.log(`✅ AI exacto 4 dígitos encontrado: ${chunk4}`)
        return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      }
      if (aiIndex.exact.has(chunk3)) {
        console.log(`✅ AI exacto 3 dígitos encontrado: ${chunk3}`)
        return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      }
      if (aiIndex.exact.has(chunk2)) {
        console.log(`✅ AI exacto 2 dígitos encontrado: ${chunk2}`)
        return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      }
      
      console.log(`❌ No se encontró AI en pos ${pos}`)
      return null
    },
    
    // Validar si una secuencia AI es realmente válida - Simplificado con regla de AI único
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      console.log(`🔍 Validando AI ${aiMatch.key} con datos: "${remainingData.substring(0, 15)}..."`)
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        console.log(`❌ AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Validación especial para AI (01) GTIN
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '240', '241', '242', '243', '253', '310', '320', '330', '340', '390', '414', '402']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          console.log(`❌ AI (10) con contenido muy corto: "${remainingData}"`)
          return false
        }
      }
      
      console.log(`✅ AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Normalizar distintos formatos de FNC1 a un único \x1D
    normalizeFNC1(s) {
      if (!s) return s;
      
      if (window.debugGS1) {
        console.log('🔧 normalizeFNC1 input:', s);
      }
      
      let result = s
        .replace(/\u001D/g, this.GS)                   // FNC1 real (ASCII 29)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS);         // si usaste texto visible
      
      // Parche mejorado: el lector está enviando "0029" en lugar del FNC1 (GS).
      // Convertir SOLO cuando aparece como separador entre un valor (numérico O
      // alfanumérico, ej. lote "JGFD55") y el siguiente AI válido — réplica del
      // fix de Ingresos (bug 1ª evaluación GS1)
      const before = result;
      result = result.replace(/([A-Za-z0-9]{1,30}?)0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37|90|91|92|93|94|95|96|97|98|99|253|310|320|330|340|390|414|402))/g, `$1${this.GS}`);
      
      if (before !== result) {
        console.log('✅ Se aplicaron reemplazos de 0029:', {
          before: before,
          after: result.replace(new RegExp(this.GS, 'g'), '<FNC1>')
        });
      } else {
        console.log('❌ NO se aplicaron reemplazos de 0029');
        
        // Intentar método alternativo: buscar patrones específicos
        if (s.includes('0029')) {
          console.log('🔧 Intentando método alternativo para 0029...');
          // Buscar patrones más específicos
          const alternativePattern = /([A-Za-z0-9]{1,30}?)0029(\d{2,4})/g;
          let match;
          while ((match = alternativePattern.exec(s)) !== null) {
            const [full, beforeValue, afterValue] = match;
            console.log('📍 Patrón encontrado:', {
              full: full,
              before: beforeValue,
              after: afterValue,
              afterLength: afterValue.length
            });
            
            // Verificar si el "after" parece un AI válido
            const commonAIs = ['10', '11', '13', '15', '17', '21', '30', '37'];
            const possibleAI = afterValue.substring(0, 2);
            if (commonAIs.includes(possibleAI)) {
              console.log('✅ Detectado AI válido:', possibleAI);
              result = result.replace(full, `${beforeValue}${this.GS}${afterValue}`);
            }
          }
        }
      }
      
      console.log('🎯 normalizeFNC1 result:', result.replace(new RegExp(this.GS, 'g'), '<FNC1>'));
      return result;
    },

    // Parser GS1 completo con regla: cada AI aparece UNA SOLA VEZ
    parseGS1(raw) {
      if (!raw) return { items: [], warnings: ["Empty input"] }
      
      // Normalización mejorada para DS2278
      let s = String(raw).trim()
      s = s.replace(/^[\]+](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = this.normalizeFNC1(s)                   // normalizar FNC1 desde escáner DS2278
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      console.log('🔍 Parseando GS1 completo:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set() // Registro de AIs ya procesados
      let i = 0
      
      while (i < s.length) {
        console.log(`🔍 Posición ${i}: Analizando "${s.slice(i, i+8)}..."`)
        
        // Saltar separadores FNC1
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          console.log(`🔍 Caracteres siguientes: "${s.slice(i, i+8)}"`)
          break
        }
        
        const { key, base, def } = m
        i += key.length
        
        // REGLA FUNDAMENTAL: Verificar si este AI ya fue procesado
        if (processedAIs.has(base)) {
          console.log(`⚠️ AI ${base} duplicado detectado - IGNORANDO (regla: un AI por código)`)
          warnings.push(`AI duplicado ${base} ignorado`)
          break // O continuar hasta el final del string como contenido del AI anterior
        }
        
        console.log(`📊 Procesando AI ${key} (${def.name}) - Primer encuentro ✓`)
        processedAIs.add(base) // Marcar como procesado
        
        let value = ""
        let ambiguous = false
        
        if (def.fixed) {
          // Longitud fija
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
        } else {
          // Longitud variable: SOLO cortar con FNC1 real o límite máximo
          const max = def.max ?? 30
          let j = i
          let cutByFNC1 = false
          
          while (j < s.length && (j - i) < max) {
            // REGLA ESTRICTA: Solo cortar con FNC1 real
            if (s[j] === this.GS) { 
              console.log(`✂️ FNC1 encontrado, cortando en posición ${j}`)
              cutByFNC1 = true
              break 
            }
            j++
          }
          
          // Si no hay FNC1, tomar todo hasta el límite máximo o fin del string
          console.log(`📏 Campo variable sin FNC1: tomando hasta posición ${j} (${j-i} chars)`)
          
          // OPCIONAL: Buscar AI solo si llegamos al límite máximo sin FNC1
          if ((j - i) >= max && j < s.length) {
            // Solo en este caso, buscar AI como posible separador
            for (let k = Math.max(i + 3, j - 10); k <= j + 2 && k < s.length - 1; k++) {
              const next = this.findAI(s, k, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(k + next.key.length)
                
                console.log(`🔍 AI potencial en límite máximo "${next.key}" en posición ${k}`)
                
                if (this.isValidAISequence(next, remainingAfterAI)) {
                  console.log(`✂️ CORTANDO por límite máximo + AI válido: "${next.key}"`)
                  j = k
                  break
                }
              }
            }
          }
          
          value = s.slice(i, j)
          i = j
          if (s[i] === this.GS) i++ // Consumir FNC1 si lo había
          
          // Detectar ambigüedad solo si no fue cortado por FNC1
          if (!cutByFNC1) {
            const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed && !processedAIs.has(nxt.base)) {
              ambiguous = true
            }
          }
        }
        
        console.log(`✅ ${def.name} extraído: "${value}"${ambiguous ? ' (ambiguous)' : ''}`)
        items.push({ ai: key, aiBase: base, name: def.name, value, ambiguous })
      }
      
      console.log(`🎯 AIs procesados: ${Array.from(processedAIs).join(', ')}`)
      return { items, warnings }
    },
    
    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      console.log('🔍 Parseando datos GS1 con parser profesional:', remainingData)
      
      try {
        // Usar el parser completo
        const result = this.parseGS1(remainingData)
        
        if (result.warnings.length > 0) {
          console.warn('⚠️ Advertencias GS1:', result.warnings)
        }
        
        // Variables temporales para fechas
        let expiryDate = null
        let productionDate = null
        let packagingDate = null
        let bestBeforeDate = null
        
        // Convertir items a formato esperado por el resto del código
        for (const item of result.items) {
          switch (item.aiBase) {
            case '02':
              // GTIN contenido en la unidad logística; viene acompañado de (37) cantidad
              variableData.content_gtin = item.value
              break
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              // Fecha de producción YYMMDD → YYYY-MM-DD
              productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '13':
              // Fecha de empaquetado YYMMDD → YYYY-MM-DD
              packagingDate = this.parseGS1Date(item.value, 'empaquetado')
              if (packagingDate) {
                variableData.packaging_date = packagingDate
              }
              break
            case '15':
              // Fecha de consumo preferente YYMMDD → YYYY-MM-DD
              bestBeforeDate = this.parseGS1Date(item.value, 'consumo preferente')
              if (bestBeforeDate) {
                variableData.best_before_date = bestBeforeDate
              }
              break
            case '17':
              // Fecha de vencimiento YYMMDD → YYYY-MM-DD
              expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
            case '37':
              variableData.count = item.value
              break
            // ========== NUEVOS AIs GS1 - FASE 2 ==========
            case '00':
              variableData.sscc = item.value
              break
            case '253':
              variableData.gdti = item.value
              break
            case '310':
            case '311':
            case '312':
            case '313':
            case '314':
            case '315':
              // Peso neto en kg (310X)
              variableData.net_weight_kg = parseFloat(item.value) || null
              break
            case '320':
            case '321':
            case '322':
            case '323':
            case '324':
            case '325':
              // Peso neto en lb (320X)
              variableData.net_weight_lb = parseFloat(item.value) || null
              break
            case '330':
            case '331':
            case '332':
            case '333':
            case '334':
            case '335':
              // Peso bruto en kg (330X)
              variableData.gross_weight_kg = parseFloat(item.value) || null
              break
            case '340':
            case '341':
            case '342':
            case '343':
            case '344':
            case '345':
              // Peso bruto en lb (340X)
              variableData.gross_weight_lb = parseFloat(item.value) || null
              break
            case '402':
              variableData.gsin = item.value
              break
            case '401':
              variableData.ginc = item.value
              break
            case '8003':
              variableData.grai = item.value
              break
            case '8004':
              variableData.giai = item.value
              break
            case '8018':
              variableData.gsrn = item.value
              break
            case '414':
              variableData.gln = item.value
              break
            // Campos personalizados (90-99)
            case '90':
              variableData.custom_field_90 = item.value
              break
            case '91':
              variableData.custom_field_91 = item.value
              break
            case '92':
              variableData.custom_field_92 = item.value
              break
            case '93':
              variableData.custom_field_93 = item.value
              break
            case '94':
              variableData.custom_field_94 = item.value
              break
            case '95':
              variableData.custom_field_95 = item.value
              break
            case '96':
              variableData.custom_field_96 = item.value
              break
            case '97':
              variableData.custom_field_97 = item.value
              break
            case '98':
              variableData.custom_field_98 = item.value
              break
            case '99':
              variableData.custom_field_99 = item.value
              break
          }
        }
        
        // Lógica de prioridad para fechas según especificaciones del usuario:
        // Si no hay fecha de vencimiento (17), usar otras fechas en el campo de vencimiento
        if (!expiryDate) {
          if (bestBeforeDate) {
            // Prioridad 1: Best before date (15)
            variableData.expiry_date = bestBeforeDate
            console.log('📅 Usando fecha de consumo preferente (15) como vencimiento:', bestBeforeDate)
          } else if (packagingDate) {
            // Prioridad 2: Packaging date (13) 
            variableData.expiry_date = packagingDate
            console.log('📅 Usando fecha de empaquetado (13) como vencimiento:', packagingDate)
          } else if (productionDate) {
            // Prioridad 3: Production date (11)
            variableData.expiry_date = productionDate
            console.log('📅 Usando fecha de producción (11) como vencimiento:', productionDate)
          }
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      console.log('🎯 Datos variables extraídos:', variableData)
      return variableData
    },
    
    findProductByGTIN(gtin) {
      console.log(`🔍 Buscando producto por GTIN: ${gtin}`)
      // Misma búsqueda que por código: normaliza GTIN-14 ↔ EAN-13 y matchea
      // también el GTIN de la caja (unidad de despacho)
      return this.findProductByBarcode(gtin)
    },
    
    findProductByBarcode(barcode) {
      console.log(`🔍 Buscando producto por código de barras: ${barcode}`)
      // .trim(): el input de escaneo con Enter arrastra un \n (bug backlog #4).
      // Además matchea GTIN-14 ↔ EAN-13 normalizando ambos a 14 dígitos.
      const clean = String(barcode || '').trim()
      const toG14 = c => String(c || '').replace(/\D/g, '').padStart(14, '0')
      const target = toG14(clean)
      // Matchea también el GTIN de la CAJA (unidad de despacho) del producto:
      // escanear la caja arma el pallet por cajas (checklist 4.2)
      const product = this.allProducts.find(p =>
        p.barcode === clean ||
        (p.barcode && /\d/.test(clean) && toG14(p.barcode) === target) ||
        (p.case_gtin && /\d/.test(clean) && toG14(p.case_gtin) === target))
      if (product) {
        console.log(`✅ Producto encontrado por código: ${product.name} (ID: ${product.id})`)
      } else {
        console.log(`❌ Producto NO encontrado por código: ${barcode}`)
      }
      return product || null
    },

    // ¿El código escaneado es el GTIN de la CAJA (unidad de despacho) del
    // producto? Distingue el escaneo por cajas del escaneo por unidades
    isCaseScan(product, code) {
      if (!product?.case_gtin) return false
      const toG14 = c => String(c || '').replace(/\D/g, '').padStart(14, '0')
      const digits = String(code || '').replace(/\D/g, '')
      if (digits.length < 8) return false
      return toG14(digits) === toG14(product.case_gtin)
    },

    async addScannedProduct(product, variableData, originalCode, resolvedGtin = null) {
      console.log(`📦 Agregando producto escaneado: ${product.name} (ID: ${product.id})`)
      
      // 1. VALIDAR INVENTARIO DISPONIBLE
      const hasInventory = await this.validateProductInventory(product.id)
      if (!hasInventory) {
        this.showError(`El producto "${product.name}" no tiene inventario disponible`)
        return
      }
      
      // ¿Se escaneó la CAJA (unidad de despacho)? — checklist 4.2: la línea
      // se arma por cajas y cada escaneo suma units_per_case unidades
      const isCase = this.isCaseScan(product, resolvedGtin || variableData.gtin || originalCode)

      // Cantidad desde el código: (37) count de unidad logística o (30) cantidad
      // variable. Un item con serial siempre es 1 unidad (cada serial es único).
      const scannedCount = variableData.serial_number ? 1 : (parseInt(variableData.count || variableData.quantity, 10) || 1)
      const scannedCases = isCase ? scannedCount : 0
      const scannedQty = isCase ? scannedCases * (product.units_per_case || 1) : scannedCount

      // 1b. VALIDAR CANTIDAD: lo escaneado + lo ya agregado no puede superar
      // el stock DISPONIBLE (sin cuarentena ni RECEPCION), total y por lote
      const shortage = this.stockShortage(product, scannedQty, variableData.lot_number || null)
      if (shortage) {
        const detail = isCase ? ` (${scannedCases} caja × ${product.units_per_case} un.)` : ''
        // Falta solo en el lote escaneado pero hay saldo en otros: ofrecerlos
        // (una caja en mano no debería bloquear el despacho si hay stock)
        const alternatives = shortage.kind === 'lot'
          ? this.lotAlternatives(product, scannedQty, variableData.lot_number)
          : []
        if (alternatives.length > 0) {
          this.lotPicker = {
            product, variableData, originalCode, resolvedGtin,
            isCase, cases: scannedCases, qty: scannedQty,
            scannedLot: variableData.lot_number,
            message: shortage.message + detail,
            alternatives
          }
          this.showLotPickerDialog = true
          return
        }
        this.showError(shortage.message + detail)
        return
      }

      // 2. VERIFICAR DUPLICADOS (mismo SKU + mismo código completo)
      const duplicateIndex = this.findDuplicateItem(product.sku, originalCode, variableData)
      if (duplicateIndex !== -1) {
        // Incrementar cantidad del item existente (por cajas si corresponde)
        const existing = this.newEgreso.items[duplicateIndex]
        existing.quantity += scannedQty
        if (isCase) {
          existing.cases_count = (existing.cases_count || 0) + scannedCases
          this.showSuccess(`Cantidad actualizada: ${product.name} ` +
            `(${existing.cases_count} cajas × ${product.units_per_case} un. = ${existing.quantity} unidades)`)
        } else {
          this.showSuccess(`Cantidad actualizada: ${product.name} (${existing.quantity} unidades)`)
        }
        console.log(`♻️ Duplicado detectado - Incrementando cantidad a ${existing.quantity}`)
        return
      }

      // 3. CREAR NUEVO ITEM
      const item = {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        quantity: scannedQty,
        // N° de cajas si la línea se armó escaneando la unidad de despacho
        cases_count: isCase ? scannedCases : null,
        lot_number: variableData.lot_number || null,
        expiry_date: variableData.expiry_date || null,
        production_date: variableData.production_date || null,
        serial_number: variableData.serial_number || null,
        barcode_scanned: originalCode,
        gtin: resolvedGtin || variableData.gtin || null,
        // Todos los AIs que traía el código (passthrough para detalle/documentos)
        gs1_items: variableData.gs1_items || null,
        picking_strategy: this.getProductStrategy(product),  // heredada del producto
        strategy_compliant: true,
        strategy_warning: null,
        // ========== NUEVOS CAMPOS GS1 - FASE 2 ==========
        sscc: variableData.sscc || null,
        pack_date: variableData.pack_date || null,
        best_before_date: variableData.best_before_date || null,
        gln: variableData.gln || null,
        gsin: variableData.gsin || null,
        gdti: variableData.gdti || null,
        net_weight_kg: variableData.net_weight_kg || null,
        net_weight_lb: variableData.net_weight_lb || null,
        gross_weight_kg: variableData.gross_weight_kg || null,
        gross_weight_lb: variableData.gross_weight_lb || null,
        // Campos personalizados (90-99)
        custom_field_90: variableData.custom_field_90 || null,
        custom_field_91: variableData.custom_field_91 || null,
        custom_field_92: variableData.custom_field_92 || null,
        custom_field_93: variableData.custom_field_93 || null,
        custom_field_94: variableData.custom_field_94 || null,
        custom_field_95: variableData.custom_field_95 || null,
        custom_field_96: variableData.custom_field_96 || null,
        custom_field_97: variableData.custom_field_97 || null,
        custom_field_98: variableData.custom_field_98 || null,
        custom_field_99: variableData.custom_field_99 || null
      }
      
      console.log(`🏷️ Item creado:`, {
        sku: item.product_sku,
        quantity: item.quantity,
        lot: item.lot_number,
        expiry: item.expiry_date,
        serial: item.serial_number,
        production: item.production_date
      })
      
      // 4. VALIDAR ESTRATEGIA
      await this.validateProductStrategy(item)
      
      // 5. AGREGAR AL LISTADO
      this.newEgreso.items.push(item)
      
      // 6. MOSTRAR NOTIFICACIÓN
      this.showSuccess(isCase
        ? `Caja agregada: ${product.name} (${scannedCases} caja × ${product.units_per_case} un.)`
        : `Producto agregado: ${product.name}`)
      
      // 7. SI NO CUMPLE LA ESTRATEGIA, MOSTRAR ADVERTENCIA
      if (!item.strategy_compliant) {
        this.showStrategyWarning(item)
      }
    },
    
    // Stock DISPONIBLE de un producto: excluye cuarentena y la zona de
    // tránsito RECEPCION (misma regla que la vista de Inventario y que el
    // backend al crear el egreso — línea roja de inventario).
    // OJO: GET /inventory trae la ubicación ANIDADA (item.location.code);
    // location_code plano solo existe en la normalización de Inventario.vue
    // Con `lot` se limita al stock de ESE lote (el código escaneado trae (10)).
    availableQuantityFor(productId, lot = null) {
      return this.inventoryData
        .filter(item => {
          const locCode = item.location_code || item.location?.code || null
          return item.product?.id === productId &&
                 !item.is_quarantined &&
                 locCode !== 'RECEPCION' &&
                 (lot === null || (item.lot_number || '') === lot)
        })
        .reduce((sum, item) => sum + (item.quantity || 0), 0)
    },

    // Cantidad de ese producto ya agregada a la salida en curso (opcionalmente
    // solo de un lote; `exceptItem` excluye la línea que se está reasignando)
    quantityInCurrentEgress(productId, lot = null, exceptItem = null) {
      return this.newEgreso.items
        .filter(i => i.product_id === productId && i !== exceptItem &&
                     (lot === null || (i.lot_number || '') === lot))
        .reduce((sum, i) => sum + (i.quantity || 0), 0)
    },

    // Valida que lo ya agregado + `qty` no supere el stock disponible del
    // producto y, si la línea trae lote, tampoco el de ese lote (25 unidades en
    // total no autorizan 20 de un lote que solo tiene 15). Devuelve
    // { kind: 'total' | 'lot', message } o null si cabe.
    stockShortage(product, qty, lot = null, exceptItem = null) {
      const disponible = this.availableQuantityFor(product.id)
      const yaAgregado = this.quantityInCurrentEgress(product.id, null, exceptItem)
      if (yaAgregado + qty > disponible) {
        return {
          kind: 'total',
          message: `Stock disponible insuficiente de "${product.name}": disponible ${disponible}, ` +
                   `en esta salida ya ${yaAgregado}, solicitado ${qty}`
        }
      }
      if (lot) {
        const dispLote = this.availableQuantityFor(product.id, lot)
        const yaLote = this.quantityInCurrentEgress(product.id, lot, exceptItem)
        if (yaLote + qty > dispLote) {
          return {
            kind: 'lot',
            message: `Stock disponible insuficiente del lote ${lot} de "${product.name}": ` +
                     `disponible ${dispLote}, en esta salida ya ${yaLote}, solicitado ${qty}`
          }
        }
      }
      return null
    },
    stockShortageMessage(product, qty, lot = null, exceptItem = null) {
      return this.stockShortage(product, qty, lot, exceptItem)?.message || null
    },

    // Otros lotes del producto con saldo (stock disponible − lo ya agregado en
    // esta salida), ordenados por vencimiento (FEFO) y luego por lote.
    // `enough`: el saldo cubre la cantidad escaneada.
    lotAlternatives(product, qty, excludeLot) {
      const byLot = {}
      this.inventoryData
        .filter(it => {
          const locCode = it.location_code || it.location?.code || null
          return it.product?.id === product.id && !it.is_quarantined && locCode !== 'RECEPCION'
        })
        .forEach(it => {
          const lot = it.lot_number || ''
          if (lot === (excludeLot || '')) return
          if (!byLot[lot]) byLot[lot] = { lot, expiry: it.expiry_date || null, stock: 0 }
          byLot[lot].stock += it.quantity || 0
          if (!byLot[lot].expiry && it.expiry_date) byLot[lot].expiry = it.expiry_date
        })
      const dateKey = e => (e ? this.toLocalDateTime(e) || '' : '9999')
      return Object.values(byLot)
        .map(a => {
          const remaining = a.stock - this.quantityInCurrentEgress(product.id, a.lot)
          return { ...a, remaining, enough: remaining >= qty }
        })
        .filter(a => a.remaining > 0)
        .sort((a, b) => dateKey(a.expiry).localeCompare(dateKey(b.expiry)) || a.lot.localeCompare(b.lot))
    },

    closeLotPicker() {
      this.showLotPickerDialog = false
      this.lotPicker = null
    },

    // El usuario eligió otro lote para el escaneo pendiente: se repite el alta
    // con el lote/vencimiento elegidos (y los AIs (10)/(17) del passthrough
    // actualizados para que el detalle no muestre el lote que no se despachó)
    async pickAlternativeLot(alt) {
      const p = this.lotPicker
      if (!p || !alt.enough) return
      const expiry = alt.expiry ? this.toLocalDateTime(alt.expiry) : null
      const yymmdd = expiry ? expiry.slice(2, 4) + expiry.slice(5, 7) + expiry.slice(8, 10) : null
      const gs1Items = Array.isArray(p.variableData.gs1_items)
        ? p.variableData.gs1_items
            .map(i => i.ai === '10' ? { ...i, value: alt.lot } : i.ai === '17' ? (yymmdd ? { ...i, value: yymmdd } : null) : i)
            .filter(i => i && !(i.ai === '10' && !alt.lot))
        : p.variableData.gs1_items
      const variableData = {
        ...p.variableData,
        lot_number: alt.lot || null,
        expiry_date: expiry,
        gs1_items: gs1Items
      }
      this.closeLotPicker()
      await this.addScannedProduct(p.product, variableData, p.originalCode, p.resolvedGtin)
      this.showInfo(`Lote ${p.scannedLot} sin saldo: la línea se despacha del lote ${alt.lot || 'sin lote'}`)
    },

    // Validar si el producto tiene inventario disponible
    async validateProductInventory(productId) {
      console.log(`🔍 Validando inventario para producto ID: ${productId}`)
      try {
        const totalQuantity = this.availableQuantityFor(productId)
        if (totalQuantity <= 0) {
          console.log(`❌ Sin stock DISPONIBLE para producto ${productId} (cuarentena/RECEPCION no cuentan)`)
          return false
        }
        console.log(`✅ Inventario disponible para producto ${productId}: ${totalQuantity} unidades`)
        return true
      } catch (error) {
        console.error(`❌ Error validando inventario para producto ${productId}:`, error)
        return false
      }
    },
    
    // Buscar item duplicado basado en SKU y código completo
    findDuplicateItem(sku, originalCode, variableData) {
      console.log(`🔍 Buscando duplicados para SKU: ${sku}, Código: ${originalCode}`)
      
      for (let i = 0; i < this.newEgreso.items.length; i++) {
        const item = this.newEgreso.items[i]
        
        // Comparar SKU y código de barras exacto
        if (item.product_sku === sku && item.barcode_scanned === originalCode) {
          // Para códigos GS1, también verificar que coincidan todos los AIs
          const sameVariableData = this.compareVariableData(item, variableData)
          
          if (sameVariableData) {
            console.log(`♻️ Duplicado encontrado en índice ${i}:`, {
              sku: item.product_sku,
              código: item.barcode_scanned,
              cantidad_actual: item.quantity
            })
            return i
          }
        }
      }
      
      console.log(`✅ No se encontraron duplicados para SKU: ${sku}`)
      return -1
    },
    
    // Comparar datos variables para determinar si son el mismo producto exacto
    compareVariableData(existingItem, newVariableData) {
      // Comparar campos clave que determinan si es el mismo producto exacto
      const fieldsToCompare = ['lot_number', 'expiry_date', 'production_date', 'serial_number']
      
      for (const field of fieldsToCompare) {
        const existingValue = existingItem[field] || null
        const newValue = newVariableData[field] || null
        
        // Si ambos valores existen y son diferentes, no es duplicado
        if (existingValue && newValue && existingValue !== newValue) {
          console.log(`🔍 Diferencia en ${field}: "${existingValue}" vs "${newValue}"`)
          return false
        }
        
        // Si solo uno tiene valor, no es duplicado
        if ((existingValue && !newValue) || (!existingValue && newValue)) {
          console.log(`🔍 Diferencia en ${field}: uno tiene valor y el otro no`)
          return false
        }
      }
      
      console.log(`✅ Los datos variables coinciden - es el mismo producto exacto`)
      return true
    },

    addProductManually() {
      // TODO: Implementar modal para agregar producto manualmente
      this.showInfo('Funcionalidad pendiente de implementar')
    },
    
    removeProduct(index) {
      this.newEgreso.items.splice(index, 1)
    },
    
    // Advertencias de estrategia
    showStrategyWarning(item) {
      this.strategyWarningMessage = item.strategy_warning
      this.suggestedProduct = item.suggested_product
      this.currentValidatingItem = item // Guardar referencia del item que está siendo validado
      this.showStrategyWarningDialog = true
    },
    
    closeStrategyWarningDialog() {
      this.showStrategyWarningDialog = false
      this.strategyWarningMessage = ''
      this.suggestedProduct = null
      this.currentValidatingItem = null
    },
    
    ignoreStrategyWarning() {
      console.log('🚫 [STRATEGY] Usuario ignora advertencia de estrategia')
      // Mantener el producto original sin cambios (ya está en la tabla)
      this.currentValidatingItem.strategy_compliant = false // Marcar como no cumple pero permitir
      this.closeStrategyWarningDialog()
      this.showInfo('Estrategia ignorada. El producto se mantendrá tal como fue escaneado.')
    },
    
    async acceptSuggestedProduct() {
      console.log('✅ [STRATEGY] Usuario acepta producto sugerido')
      
      if (!this.currentValidatingItem || !this.suggestedProduct) {
        this.showError('Error: No se encontró información del producto sugerido')
        return
      }
      
      // Actualizar el item con los datos sugeridos
      const item = this.currentValidatingItem
      const suggested = this.suggestedProduct

      console.log('🔄 [STRATEGY] Aplicando sugerencia:', suggested)

      // Lote/vencimiento originales: se restauran si el lote sugerido no tiene
      // stock para la línea, si no se pudo mapear la sugerencia o ante
      // cualquier error (la línea NUNCA debe quedar con el lote escaneado y el
      // vencimiento de otro lote)
      const before = { lot_number: item.lot_number, expiry_date: item.expiry_date }
      const restore = () => {
        item.lot_number = before.lot_number
        item.expiry_date = before.expiry_date
      }

      try {
        let mapped = true
        if (suggested.type === 'expiry_date') {
          // Fecha como YYYY-MM-DDT00:00:00 (hora LOCAL): el backend entrega la
          // fecha sin hora y `new Date('2026-10-07')` la interpreta en UTC, lo
          // que en Chile la mostraba como el día anterior (06/10)
          item.expiry_date = this.toLocalDateTime(suggested.value)
          console.log(`📅 [STRATEGY] Fecha de vencimiento actualizada: ${item.expiry_date}`)

          // CRÍTICO: También actualizar el lot_number correspondiente a esta fecha de vencimiento
          console.log(`🔍 [STRATEGY] Buscando lote correspondiente a fecha ${suggested.value}`)
          mapped = await this.findCorrectLotForExpiryDate(item, suggested.value)
        } else if (suggested.type === 'lot') {
          // Actualizar lote
          const oldLotNumber = item.lot_number
          item.lot_number = suggested.value
          console.log(`🏷️ [STRATEGY] Lote actualizado: ${oldLotNumber} → ${item.lot_number}`)

          // CRÍTICO: También actualizar expiry_date correspondiente al nuevo lote
          console.log(`🔍 [STRATEGY] Buscando fecha de vencimiento correspondiente al lote ${suggested.value}`)
          mapped = await this.findCorrectExpiryDateForLot(item, suggested.value)
        }

        if (!mapped) {
          restore()
          this.closeStrategyWarningDialog()
          this.showError('No se pudo ubicar en el inventario el lote/vencimiento sugerido. La línea se mantiene como fue escaneada.')
          return
        }

        // El lote sugerido debe tener stock para TODA la línea (más lo que ya
        // se agregó de ese lote en esta salida)
        const product = this.allProducts.find(p => p.id === item.product_id)
        const shortage = product && item.lot_number
          ? this.stockShortageMessage(product, item.quantity || 0, item.lot_number, item)
          : null
        if (shortage) {
          restore()
          this.closeStrategyWarningDialog()
          this.showError(`${shortage}. La línea se mantiene con el lote escaneado.`)
          return
        }
      } catch (error) {
        console.error('❌ [STRATEGY] Error aplicando sugerencia:', error)
        restore()
        this.closeStrategyWarningDialog()
        this.showError('No se pudo aplicar la sugerencia. La línea se mantiene como fue escaneada.')
        return
      }

      // Marcar como cumpliendo la estrategia
      item.strategy_compliant = true
      item.strategy_warning = null
      item.suggested_product = null

      this.closeStrategyWarningDialog()
      this.showSuccess('Producto actualizado para cumplir con la estrategia.')
    },

    // Normaliza cualquier fecha a "YYYY-MM-DDT00:00:00" — el mismo formato que
    // produce parseGS1Date — SIN correr el día por zona horaria:
    //  - "2026-10-07" / ISO con hora → se toma la parte de fecha tal cual
    //  - "Fri, 09 Oct 2026 00:00:00 GMT" (jsonify de Flask) o "...Z" → una fecha
    //    "sin hora" expresada en UTC: se toman los componentes UTC (en Chile,
    //    los locales darían el día anterior)
    //  - Date u otros → componentes locales
    toLocalDateTime(value) {
      if (!value) return null
      const pad = n => String(n).padStart(2, '0')
      const s = value instanceof Date ? null : String(value).trim()
      const m = s && s.match(/^(\d{4}-\d{2}-\d{2})/)
      if (m) return `${m[1]}T00:00:00`
      const d = new Date(value)
      if (isNaN(d.getTime())) return null
      if (s && /(GMT|UTC|Z)$/i.test(s)) {
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T00:00:00`
      }
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`
    },
    
    // Función para encontrar el lot_number correcto basado en expiry_date
    async findCorrectLotForExpiryDate(item, targetExpiryDate) {
      try {
        console.log(`🔍 [LOT_FINDER] Buscando lote para expiry_date: ${targetExpiryDate} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [LOT_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [LOT_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [LOT_FINDER] Target expiry_date: "${targetExpiryDate}"`)
          
          // Normalizar la fecha objetivo para comparación (solo YYYY-MM-DD,
          // sin conversión a UTC que corra el día)
          const normalizeDate = (dateStr) => {
            const local = this.toLocalDateTime(dateStr)
            return local ? local.substring(0, 10) : null
          }
          
          const targetDateNormalized = normalizeDate(targetExpiryDate)
          console.log(`🎯 [LOT_FINDER] Target date normalized: "${targetDateNormalized}"`)
          
          // Buscar el item que tenga exactamente la fecha de vencimiento objetivo
          const matchingItem = availableData.items.find(detailItem => {
            if (!detailItem.expiry_date) return false
            
            const detailDateNormalized = normalizeDate(detailItem.expiry_date)
            
            console.log(`🔄 [LOT_FINDER] Comparing item:`)
            console.log(`   - lot_number: "${detailItem.lot_number}"`)
            console.log(`   - expiry_date: "${detailItem.expiry_date}"`)
            console.log(`   - normalized: "${detailDateNormalized}"`)
            console.log(`   - match? ${detailDateNormalized === targetDateNormalized}`)
            
            return detailDateNormalized === targetDateNormalized
          })
          
          if (matchingItem) {
            console.log(`✅ [LOT_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar tanto lote como fecha para asegurar consistencia
            const oldLotNumber = item.lot_number
            const oldExpiryDate = item.expiry_date
            
            item.lot_number = matchingItem.lot_number
            
            // Fecha local sin hora (no toISOString: corre el día en UTC-3/-4)
            const normalizedTargetDate = this.toLocalDateTime(targetExpiryDate)
            item.expiry_date = normalizedTargetDate
            
            console.log(`🔄 [LOT_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - lot_number: "${oldLotNumber}" → "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${normalizedTargetDate}"`)
            return true
          } else {
            console.log(`❌ [LOT_FINDER] No se encontró item con expiry_date: ${targetExpiryDate}`)
            console.log(`🔍 [LOT_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}", normalized: "${normalizeDate(item.expiry_date)}"`)
            })
          }
        } else {
          console.log(`❌ [LOT_FINDER] No hay información detallada de items en la respuesta`)
        }

      } catch (error) {
        console.error('❌ [LOT_FINDER] Error buscando lote correcto:', error)
      }
      return false
    },
    
    // Función para encontrar el expiry_date correcto basado en lot_number  
    async findCorrectExpiryDateForLot(item, targetLotNumber) {
      try {
        console.log(`🔍 [EXPIRY_FINDER] Buscando fecha de vencimiento para lote: ${targetLotNumber} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [EXPIRY_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [EXPIRY_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [EXPIRY_FINDER] Target lot_number: "${targetLotNumber}"`)
          
          // Buscar el item que tenga exactamente el lote objetivo
          const matchingItem = availableData.items.find(detailItem => {
            return detailItem.lot_number === targetLotNumber
          })
          
          if (matchingItem) {
            console.log(`✅ [EXPIRY_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar fecha de vencimiento para consistencia
            const oldExpiryDate = item.expiry_date
            
            // Mantener formato ISO para consistency
            if (matchingItem.expiry_date) {
              item.expiry_date = this.toLocalDateTime(matchingItem.expiry_date)
            } else {
              item.expiry_date = null
            }
            
            console.log(`🔄 [EXPIRY_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${item.expiry_date}"`)
            return true
          } else {
            console.log(`❌ [EXPIRY_FINDER] No se encontró item con lot_number: ${targetLotNumber}`)
            console.log(`🔍 [EXPIRY_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}"`)
            })
          }
        } else {
          console.log(`❌ [EXPIRY_FINDER] No hay información detallada de items en la respuesta`)
        }

      } catch (error) {
        console.error('❌ [EXPIRY_FINDER] Error buscando fecha de vencimiento correcta:', error)
      }
      return false
    },
    
    // CRUD operations
    async saveEgreso() {
      if (!this.canSaveEgreso) return

      // Pallet opcional: mínimo 2 elementos (mensaje del auditor GS1)
      if (this.newEgreso.build_pallet) {
        const totalUnits = this.newEgreso.items.reduce((s, i) => s + (i.quantity || 0), 0)
        if (totalUnits < 2) {
          this.showError('Un pallet SSCC requiere mínimo 2 elementos: no puedes añadir solo un elemento. ' +
            'Desmarca "Armar pallet (SSCC)" para un egreso unitario.')
          return
        }
      }

      try {
        this.submitting = true

        const usaDestino = this.sectorSalud && this.salidaModo === 'destino'
        const data = {
          customer_id: usaDestino ? null : this.newEgreso.customer_id,
          destination_id: usaDestino ? this.newEgreso.destination_id : null,
          strategy: this.newEgreso.strategy,
          notes: this.newEgreso.notes,
          items: this.newEgreso.items,
          build_pallet: this.newEgreso.build_pallet,
          net_weight_kg: this.newEgreso.net_weight_kg || null,
          gross_weight_kg: this.newEgreso.gross_weight_kg || null
        }
        
        console.log('📤 [SAVE EGRESO] Sending data to backend:')
        console.log('🏢 Customer ID:', data.customer_id)
        console.log('📋 Strategy:', data.strategy)
        console.log('📝 Notes:', data.notes)
        console.log('📦 Items:', data.items)
        data.items.forEach((item, index) => {
          console.log(`   Item ${index + 1}:`, {
            product_id: item.product_id,
            sku: item.sku,
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date,
            strategy_compliant: item.strategy_compliant
          })
        })
        
        const response = await api.post('/egresos/egress-orders', data)
        
        if (response.data) {
          this.showSuccess('Orden de egreso creada exitosamente')
          this.closeCreateModal()
          await this.loadEgresos()
        }
        
      } catch (error) {
        console.error('Error saving egreso:', error)
        const errorMessage = error.response?.data?.error || 'Error al crear orden de egreso'
        this.showError(errorMessage)
      } finally {
        this.submitting = false
      }
    },
    
    // ===== Documentos GS1 del egreso (checklist 4.4) =====
    async downloadEgressPackingList(egreso) {
      try {
        const response = await api.downloadEgressPackingListPdf(egreso.id)
        this.downloadFileBlob(response.data, `packing_list_${egreso.order_number}.pdf`)
      } catch (e) {
        console.error('Error descargando packing list PDF:', e)
        this.showError('Error generando el Packing List PDF')
      }
    },
    async downloadEgressHierarchy(egreso) {
      try {
        const response = await api.downloadEgressHierarchyPdf(egreso.id)
        this.downloadFileBlob(response.data, `jerarquia_gs1_${egreso.order_number}.pdf`)
      } catch (e) {
        console.error('Error descargando jerarquía GS1:', e)
        this.showError('Error generando el informe de jerarquía GS1')
      }
    },
    async downloadEgressPackingListJson(egreso) {
      try {
        const { data } = await api.getEgressPackingList(egreso.id)
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        this.downloadFileBlob(blob, `packing_list_${egreso.order_number}.json`)
        const ref = data.shipment?.sscc ? `SSCC ${data.shipment.sscc}` : `ref. interna ${data.shipment?.order_number} (sin prefijo GS1 no se emite SSCC)`
        this.showSuccess(`Packing List electrónico generado (${ref}) — importable en Recepciones`)
      } catch (e) {
        console.error('Error descargando packing list JSON:', e)
        this.showError('Error generando el Packing List electrónico')
      }
    },
    // Abre el diálogo previo de la etiqueta (AI 90-99 de uso interno opcional)
    async printEgressSsccLabel(egreso) {
      this.ssccLabelDialog = { show: true, egreso, ai: '91', value: '', aiOrder: [], packingList: null }
      try {
        const { data } = await api.getEgressPackingList(egreso.id)
        if (this.ssccLabelDialog.egreso === egreso) this.ssccLabelDialog.packingList = data
      } catch (e) {
        console.warn('No se pudo precargar el packing list para los chips de AIs:', e)
      }
    },
    // Reordenar los AIs del GS1-128 de contenido (chips del diálogo)
    moveSsccAi(index, dir) {
      const order = [...this.ssccAiList]
      const j = index + dir
      if (j < 0 || j >= order.length) return
      const tmp = order[index]
      order[index] = order[j]
      order[j] = tmp
      this.ssccLabelDialog.aiOrder = order
    },
    resetSsccAiOrder() {
      this.ssccLabelDialog.aiOrder = []
    },
    ssccLabelOptions() {
      return { aiOrder: this.ssccLabelDialog.aiOrder, maxChars: 48 }
    },
    async confirmSsccLabel() {
      const { egreso, ai, value } = this.ssccLabelDialog
      this.ssccLabelDialog.show = false
      try {
        const { data } = await api.getEgressPackingList(egreso.id)
        // Pallet mínimo 2 elementos (checklist GS1): con 1 unidad no hay SSCC —
        // se avisa y la etiqueta sale como egreso unitario (referencia interna)
        if (data.shipment?.sscc_blocked_reason) {
          this.showError(data.shipment.sscc_blocked_reason)
        }
        const extraAI = value ? { ai, value } : null
        const html = buildLogisticLabelHTML(data, extraAI, this.ssccLabelOptions())
        const w = window.open('', '_blank', 'width=500,height=750')
        w.document.write(html)
        w.document.close()
      } catch (e) {
        console.error('Error generando etiqueta SSCC:', e)
        this.showError('Error generando la etiqueta SSCC')
      }
    },
    // Export a Excel del reporte GS1 de salidas (checklist 4.1/4.3), respetando
    // los filtros activos; la clave GS1 se clasifica por largo (18=SSCC,
    // 17=GSIN, 8-14=GTIN, resto=lote)
    async exportGs1Excel() {
      try {
        const params = new URLSearchParams({ direction: 'out', format: 'xlsx' })
        if (this.filters.dateFrom) params.set('date_from', this.filters.dateFrom)
        if (this.filters.dateTo) params.set('date_to', this.filters.dateTo)
        const g = (this.filters.gs1 || '').trim()
        if (g) {
          if (/^\d{18}$/.test(g)) params.set('sscc', g)
          else if (/^\d{17}$/.test(g)) params.set('gsin', g)
          else if (/^\d{8,14}$/.test(g)) params.set('gtin', g)
          else params.set('lot_number', g)
        }
        const response = await api.downloadGs1MovementsXlsx(params.toString())
        this.downloadFileBlob(response.data, 'movimientos_gs1_salidas.xlsx')
        this.showSuccess('Reporte GS1 de salidas exportado a Excel')
      } catch (e) {
        console.error('Error exportando Excel GS1:', e)
        this.showError('Error exportando el reporte GS1')
      }
    },
    // Etiqueta en ZPL nativo (Zebra): sin driver ni reescalado — la impresora
    // dibuja las barras exactas por dot. Imprimir: lp -d <cola> -o raw archivo.zpl
    async downloadSsccLabelZpl() {
      const { egreso, ai, value } = this.ssccLabelDialog
      this.ssccLabelDialog.show = false
      try {
        const { data } = await api.getEgressPackingList(egreso.id)
        if (data.shipment?.sscc_blocked_reason) {
          this.showError(data.shipment.sscc_blocked_reason)
        }
        const extraAI = value ? { ai, value } : null
        // Perfil ZPL de la impresora elegida en este equipo (sin elección: moderno)
        const zpl = buildLogisticLabelZPL(data, extraAI, profileForPrinter(getSavedPrinter()), this.ssccLabelOptions())
        const nombre = `etiqueta_sscc_${data.shipment?.sscc || egreso.order_number}.zpl`
        this.downloadFileBlob(new Blob([zpl], { type: 'text/plain' }), nombre)
        this.showSuccess(`ZPL generado (${nombre}) — imprímelo en crudo: lp -d <impresora> -o raw ${nombre}`)
      } catch (e) {
        console.error('Error generando ZPL:', e)
        this.showError('Error generando el ZPL de la etiqueta')
      }
    },
    // Impresión DIRECTA vía Zebra Browser Print (app local): el ZPL viaja del
    // navegador a la impresora sin diálogo ni terminal. La 1ª vez, Browser
    // Print pide autorizar este sitio — hay que ACEPTAR ese aviso.
    async printSsccLabelZebra() {
      const { egreso, ai, value } = this.ssccLabelDialog
      this.zebraPrinting = true
      try {
        const { data } = await api.getEgressPackingList(egreso.id)
        if (data.shipment?.sscc_blocked_reason) {
          this.showError(data.shipment.sscc_blocked_reason)
        }
        const extraAI = value ? { ai, value } : null
        // Resolver ANTES la impresora (elegida en este equipo o la por defecto)
        // para generar el ZPL con su perfil (la 2844-Z no admite UTF-8)
        const target = await resolveBrowserPrintTarget()
        const zpl = buildLogisticLabelZPL(data, extraAI, target.profile, this.ssccLabelOptions())
        const printerName = await printZplViaBrowserPrint(zpl, target)
        this.ssccLabelDialog.show = false
        this.showSuccess(`Etiqueta enviada a "${printerName}" vía Browser Print`)
      } catch (e) {
        console.error('Error imprimiendo vía Browser Print:', e)
        if (e.code === 'no-service') {
          this.showError('Zebra Browser Print no responde: verifica que la app esté corriendo, ' +
            'que tenga una impresora predeterminada configurada, y ACEPTA el aviso ' +
            'de autorización del sitio si aparece. Alternativa: botón "Descargar ZPL".')
        } else if (e.code === 'printer-missing') {
          this.showError(`${e.message}: conéctala o elige otra en el selector de impresora`)
        } else {
          this.showError(`Browser Print no pudo imprimir (${e.message}). Revisa la consola (F12) o usa "Descargar ZPL"`)
        }
      } finally {
        this.zebraPrinting = false
      }
    },
    toggleGsinSelection(id) {
      const i = this.gsinSelection.indexOf(id)
      if (i >= 0) this.gsinSelection.splice(i, 1)
      else this.gsinSelection.push(id)
    },
    async downloadMultiPackingList() {
      // Packing list múltiple: agrupa ≥2 SSCC bajo un GSIN (checklist GS1)
      if (this.gsinSelection.length < 2) {
        this.showError('Un GSIN agrupa mínimo 2 SSCC: selecciona al menos 2 egresos')
        return
      }
      try {
        const ids = this.gsinSelection.join(',')
        // Versión imprimible con CÓDIGOS DE BARRAS (bwip-js): GS1-128 (00)
        // por pallet + GS1-128 (402) del GSIN al final
        const { data } = await api.getMultiPackingList(ids)
        const html = buildMultiPackingListHTML(data)
        const w = window.open('', '_blank', 'width=900,height=1000')
        w.document.write(html)
        w.document.close()
        this.showSuccess('Packing List múltiple generado con GSIN — el GSIN acompaña la documentación, no se pega en los bultos')
        this.gsinSelection = []
        this.loadEgresos()
      } catch (e) {
        // El backend responde 422 con el motivo (mínimo 2 SSCC / egresos unitarios)
        let msg = e.response?.data?.error || 'Error generando el Packing List múltiple'
        try {
          const text = await e.response?.data?.text?.()
          if (text) msg = JSON.parse(text).error || msg
        } catch (_) { /* respuesta no JSON */ }
        console.error('Error packing list múltiple:', e)
        this.showError(msg)
      }
    },
    downloadFileBlob(blobData, filename) {
      const url = window.URL.createObjectURL(blobData instanceof Blob ? blobData : new Blob([blobData]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    viewEgreso(egreso) {
      // TODO: Implementar modal de visualización
      console.log('View egreso:', egreso)
      this.showInfo('Vista de detalle pendiente de implementar')
    },
    
    viewEgreso(egreso) {
      this.selectedEgreso = egreso
      this.showReportModal = true
    },
    
    printReport() {
      // Crear una ventana nueva para imprimir
      const printWindow = window.open('', '_blank')
      const reportContent = this.generatePrintableReport()
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Salida - ${this.selectedEgreso.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .info-section { margin-bottom: 20px; }
            .info-section h3 { margin-bottom: 10px; border-bottom: 2px solid #333; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .summary { margin-top: 20px; }
            .summary-item { display: flex; justify-content: space-between; margin-bottom: 5px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${reportContent}
        </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
      printWindow.close()
    },
    
    generatePrintableReport() {
      if (!this.selectedEgreso) return ''
      
      const items = this.selectedEgreso.items || []
      const totalSKUs = items.length
      const totalUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      return `
        <div class="header">
          <h1>REPORTE DE SALIDA</h1>
          <h2>Orden: ${this.selectedEgreso.order_number}</h2>
        </div>
        
        <div class="info-section">
          <h3>Información General</h3>
          <p><strong>Fecha:</strong> ${this.formatDate(this.selectedEgreso.created_at)}</p>
          <p><strong>Cliente:</strong> ${this.selectedEgreso.customer?.name || 'No especificado'}</p>
          <p><strong>RUT:</strong> ${this.selectedEgreso.customer?.tax_id || '-'}</p>
          <p><strong>Estrategia:</strong> ${this.getStrategyText(this.selectedEgreso.strategy)}</p>
          <p><strong>Usuario:</strong> ${this.selectedEgreso.user || 'Sistema'}</p>
          <p><strong>Notas:</strong> ${this.selectedEgreso.notes || 'Sin notas'}</p>
        </div>
        
        <div class="info-section">
          <h3>Productos</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>GTIN</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
                <th>F. Empaque</th>
                <th>SSCC</th>
                <th>GLN</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.product?.name || 'Producto desconocido'}</td>
                  <td>${item.gtin || item.product?.gtin || item.product?.barcode || '-'}</td>
                  <td>${item.quantity}</td>
                  <td>${item.lot_number || '-'}</td>
                  <td>${this.formatDate(item.expiry_date) || '-'}</td>
                  <td>${item.serial_number || '-'}</td>
                  <td>${this.formatDate(item.production_date) || '-'}</td>
                  <td>${this.formatDate(item.pack_date) || '-'}</td>
                  <td>${item.sscc || '-'}</td>
                  <td>${item.gln || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="summary">
          <h3>Resumen</h3>
          <div class="summary-item">
            <strong>Total de ítems:</strong>
            <span>${totalSKUs}</span>
          </div>
          <div class="summary-item">
            <strong>Total de Unidades:</strong>
            <span>${totalUnits}</span>
          </div>
        </div>
      `
    },
    
    async deleteEgreso(egreso) {
      if (!confirm(`¿Está seguro de eliminar la orden ${egreso.order_number}?`)) {
        return
      }
      
      try {
        await api.delete(`/egresos/egress-orders/${egreso.id}`)
        this.showSuccess('Orden eliminada exitosamente')
        await this.loadEgresos()
      } catch (error) {
        console.error('Error deleting egreso:', error)
        this.showError('Error al eliminar orden')
      }
    },
    
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        // Una fecha sin hora ("2026-10-07") se interpreta como UTC y en Chile
        // se mostraba el día anterior: se fija a medianoche LOCAL. Los
        // timestamps (created_at) se dejan tal cual.
        const s = String(dateString)
        const local = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s
        return new Date(local).toLocaleDateString('es-ES')
      } catch {
        return dateString
      }
    },
    
    getStrategyColor(strategy) {
      const colors = {
        'libre': 'grey',
        'fifo': 'blue',
        'fefo': 'orange',
        'lifo': 'green'
      }
      return colors[strategy] || 'grey'
    },
    
    getStrategyText(strategy) {
      const texts = {
        'libre': 'Libre',
        'fifo': 'FIFO',
        'fefo': 'FEFO',
        'lifo': 'LIFO'
      }
      return texts[strategy] || strategy
    },

    // ========== UTILIDADES PARA NUEVOS CAMPOS GS1 ==========
    
    hasWeightOrCustomData(item) {
      return !!(item.gross_weight_kg || item.gross_weight_lb || 
               item.net_weight_kg || item.net_weight_lb ||
               item.gdti || this.hasCustomFields(item))
    },
    
    hasCustomFields(item) {
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      return customFields.some(field => item[field])
    },
    
    getCustomFieldsCount(item) {
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      return customFields.filter(field => item[field]).length
    },

    // ========== FUNCIONES INFORMATIVAS PARA CÓDIGOS NO COMERCIALES ==========
    
    showCodeInformation(analysis, productData) {
      const codeType = this.identifyCodeType(analysis, productData)
      const details = this.formatCodeDetails(productData)
      
      const message = `📊 Código ${codeType} detectado:\n${details}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo por 5 segundos
      this.showInfoMessage(message, 5000)
      
      console.log(`📊 [INFO] Código no comercial parseado:`, {
        type: codeType,
        gtin: analysis.gtin,
        data: productData
      })
    },
    
    showBasicCodeInformation(analysis) {
      const codeType = this.identifyCodeTypeByGTIN(analysis.gtin)
      const message = `📦 Código ${codeType} detectado:\n🏷️ GTIN: ${analysis.gtin}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo por 5 segundos
      this.showInfoMessage(message, 5000)
      
      console.log(`📦 [INFO] Código GS1 básico:`, {
        type: codeType,
        gtin: analysis.gtin
      })
    },
    
    identifyCodeType(analysis, productData) {
      // Identificar tipo de código basado en los AIs presentes
      if (productData.sscc) return 'CONTENEDOR (SSCC)'
      if (productData.gsin) return 'ENVÍO (GSIN)'  
      if (productData.gdti) return 'DOCUMENTO (GDTI)'
      if (productData.gln) return 'UBICACIÓN (GLN)'
      if (productData.gross_weight_kg || productData.net_weight_kg) return 'PRODUCTO CON PESO'
      if (productData.custom_field_90 || productData.custom_field_91) return 'CÓDIGO PERSONALIZADO'
      if (analysis.gtin) return 'PRODUCTO GS1'
      
      return 'GS1'
    },
    
    identifyCodeTypeByGTIN(gtin) {
      if (!gtin) return 'GS1'
      
      // Análisis básico del GTIN para identificar tipo
      if (gtin.startsWith('00')) return 'CONTENEDOR/PALLET'
      if (gtin.startsWith('02')) return 'PRODUCTO VARIABLE'
      if (gtin.startsWith('1') || gtin.startsWith('2')) return 'PRODUCTO COMERCIAL'
      
      return 'PRODUCTO GS1'
    },
    
    formatCodeDetails(productData) {
      const details = []
      
      // Información principal
      if (productData.sscc) details.push(`📦 SSCC: ${productData.sscc}`)
      if (productData.gsin) details.push(`🚛 GSIN: ${productData.gsin}`)
      if (productData.gdti) details.push(`📄 GDTI: ${productData.gdti}`)
      if (productData.gln) details.push(`🏢 GLN: ${productData.gln}`)
      
      // Información de producto
      if (productData.lot_number) details.push(`🏷️ Lote: ${productData.lot_number}`)
      if (productData.serial_number) details.push(`🔢 Serie: ${productData.serial_number}`)
      if (productData.expiry_date) details.push(`📅 Vence: ${this.formatDate(productData.expiry_date)}`)
      if (productData.production_date) details.push(`🏭 Prod.: ${this.formatDate(productData.production_date)}`)
      
      // Pesos
      if (productData.gross_weight_kg) details.push(`⚖️ Peso bruto: ${productData.gross_weight_kg}kg`)
      if (productData.net_weight_kg) details.push(`⚖️ Peso neto: ${productData.net_weight_kg}kg`)
      if (productData.gross_weight_lb) details.push(`⚖️ Peso bruto: ${productData.gross_weight_lb}lb`)
      if (productData.net_weight_lb) details.push(`⚖️ Peso neto: ${productData.net_weight_lb}lb`)
      
      // Campos personalizados
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      
      customFields.forEach((field, index) => {
        if (productData[field]) {
          details.push(`🔧 Campo ${90 + index}: ${productData[field]}`)
        }
      })
      
      return details.length > 0 ? details.join('\n') : '📋 Información GS1 detectada'
    },
    
    showInfoMessage(message, duration = 5000) {
      // Usar el sistema de snackbar existente
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: duration
      }
    },
    

    
    // Notificaciones
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      }
    },
    
    showInfo(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: 4000
      }
    },
    
    // Métodos de validación de estrategias
    validateFIFOStrategy(item, availableItems) {
      console.log(`🔍 [FIFO] Validating item:`, item)
      console.log(`📦 [FIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [FIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [FIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [FIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más antigua primero)
      const sortedLots = [...lots].sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
      console.log(`🗂️ [FIFO] Sorted lots (oldest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [FIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [FIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más antiguos con stock disponible
      const olderLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) < new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [FIFO] Older lots with stock:`, olderLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (olderLotsWithStock.length > 0) {
        const oldestLot = olderLotsWithStock[0]
        console.log(`❌ [FIFO] Strategy violation! Suggesting oldest lot:`, oldestLot)
        return {
          compliant: false,
          warning: `FIFO: Hay ${olderLotsWithStock.length} lote(s) con fecha de ingreso más antigua disponible(s). El más antiguo es '${oldestLot.value}' con ${oldestLot.quantity} unidades (ingreso: ${this.formatDate(oldestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: oldestLot.value,
            date: oldestLot.entry_date,
            available_quantity: oldestLot.quantity
          }
        }
      }
      
      console.log(`✅ [FIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateLIFOStrategy(item, availableItems) {
      console.log(`🔍 [LIFO] Validating item:`, item)
      console.log(`📦 [LIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [LIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [LIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [LIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más reciente primero)
      const sortedLots = [...lots].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
      console.log(`🗂️ [LIFO] Sorted lots (newest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [LIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [LIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más recientes con stock disponible
      const newerLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) > new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [LIFO] Newer lots with stock:`, newerLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (newerLotsWithStock.length > 0) {
        const newestLot = newerLotsWithStock[0]
        console.log(`❌ [LIFO] Strategy violation! Suggesting newest lot:`, newestLot)
        return {
          compliant: false,
          warning: `LIFO: Hay ${newerLotsWithStock.length} lote(s) con fecha de ingreso más reciente disponible(s). El más reciente es '${newestLot.value}' con ${newestLot.quantity} unidades (ingreso: ${this.formatDate(newestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: newestLot.value,
            date: newestLot.entry_date,
            available_quantity: newestLot.quantity
          }
        }
      }
      
      console.log(`✅ [LIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateFEFOStrategy(item, availableItems) {
      if (!item.expiry_date) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const expiryDates = availableItems.expiry_dates || []
      if (expiryDates.length === 0) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      // Ordenar por fecha de vencimiento (más próxima primero)
      const sortedExpiry = [...expiryDates].sort((a, b) => new Date(a.expiry_date || a.value) - new Date(b.expiry_date || b.value))
      
      // Normalizar fecha del item escaneado para comparación (solo fecha, sin hora)
      const scannedDateStr = item.expiry_date
      const scannedDateOnly = scannedDateStr ? scannedDateStr.substring(0, 10) : ''
      
      // Encontrar la fecha de vencimiento escaneada (comparando solo la parte de fecha)
      const scannedExpiry = expiryDates.find(exp => {
        const expDateStr = exp.expiry_date || exp.value || ''
        const expDateOnly = expDateStr.substring(0, 10)
        return expDateOnly === scannedDateOnly
      })
      
      if (!scannedExpiry) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const scannedDate = new Date(scannedExpiry.expiry_date || scannedExpiry.value)
      
      // Verificar si hay fechas de vencimiento más próximas con stock disponible
      const earlierExpiryWithStock = sortedExpiry.filter(exp => {
        const expDate = new Date(exp.expiry_date || exp.value)
        const hasStock = exp.quantity > 0
        const isEarlier = expDate < scannedDate
        return isEarlier && hasStock
      })
      
      if (earlierExpiryWithStock.length > 0) {
        const earliestExpiry = earlierExpiryWithStock[0]
        return {
          compliant: false,
          warning: `FEFO: Hay ${earlierExpiryWithStock.length} fecha(s) de vencimiento más próxima(s) disponible(s). La más próxima vence el ${this.formatDate(earliestExpiry.expiry_date || earliestExpiry.value)} con ${earliestExpiry.quantity} unidades`,
          suggestedItem: {
            type: 'expiry_date',
            value: earliestExpiry.expiry_date || earliestExpiry.value,
            available_quantity: earliestExpiry.quantity
          }
        }
      }
      
      return { compliant: true, warning: null, suggestedItem: null }
    }
  }
}
</script>

<style scoped>
.egresos-container {
  padding: 20px;
}

.egresos-title {
  font-size: 1.5rem;
  font-weight: 500;
}

.egresos-filters .v-col {
  padding: 4px 8px;
}

.egresos-table {
  margin-top: 16px;
}

/* Estilos para la tabla de items */
.egresos-items-table {
  width: 100%;
  table-layout: fixed;
}

.egresos-items-table th,
.egresos-items-table td {
  text-align: left;
  vertical-align: middle;
  padding: 8px 12px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.egresos-items-table .product-col {
  width: 25%;
  min-width: 200px;
}

.egresos-items-table .sku-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .quantity-col {
  width: 8%;
  min-width: 80px;
  text-align: center;
}

.egresos-items-table .lot-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .expiry-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .serial-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .production-col {
  width: 8%;
  min-width: 90px;
}

.egresos-items-table .pack-col {
  width: 8%;
  min-width: 90px;
}

.egresos-items-table .sscc-col {
  width: 8%;
  min-width: 100px;
  font-size: 0.8em;
}

.egresos-items-table .gln-col {
  width: 7%;
  min-width: 80px;
  font-size: 0.8em;
}

.egresos-items-table .strategy-col {
  width: 8%;
  min-width: 90px;
  text-align: center;
}

.egresos-items-table .actions-col {
  width: 5%;
  min-width: 60px;
  text-align: center;
}

/* Estilos para el modal de reporte */
.report-content {
  max-height: 70vh;
  overflow-y: auto;
}

.report-header h3 {
  color: #1976d2;
  margin-bottom: 15px;
}

.report-table {
  font-size: 0.9em;
}

.report-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.report-summary {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.report-summary > div {
  padding: 5px 0;
}

/* Responsividad */
@media (max-width: 768px) {
  .egresos-container {
    padding: 10px;
  }
  
  .egresos-filters .v-col {
    padding: 2px 4px;
  }
}
</style> 